import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { localizedName, localizedNameWithEn } from '../utils/localizedName'

import {
  Box, Text, Button, Table, Group, Modal, Stack, Select,
  TextInput, ActionIcon, Tooltip, Center, Loader, Badge,
  Alert, Tabs, Divider, SimpleGrid, Paper, FileButton, ScrollArea, List,
  Image, Textarea, Drawer, NumberInput, Switch, Menu, Card,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useAuth } from '../hooks/useAuth'
import {
  IconPlus, IconEdit, IconTrash, IconAlertCircle, IconBan,
  IconBuilding, IconCategory, IconTag, IconChevronRight, IconMapPin,
  IconFileSpreadsheet, IconUpload, IconCheck, IconUser, IconBriefcase,
  IconDeviceLaptop, IconVersions, IconPhoto, IconX, IconSearch, IconTruck, IconMail,
  IconSettings, IconChevronDown, IconUsersGroup, IconBox, IconApps, IconListCheck,
  IconCode, IconTemplate, IconServer, IconMenu2, IconBell, IconPlus as IconPlusSmall, IconTrash as IconTrashSmall, IconBuildingWarehouse, IconClock, IconDotsVertical,
} from '@tabler/icons-react'
import {
  getDepartments, createDepartment, updateDepartment, deleteDepartment, deactivateDepartment,
  getCategories, createCategory, updateCategory, deleteCategory, deactivateCategory,
  getSubCategories, createSubCategory, updateSubCategory, deleteSubCategory, deactivateSubCategory,
  getLocations, createLocation, updateLocation, deleteLocation, deactivateLocation,
  getPositions, createPosition, updatePosition, deletePosition, deactivatePosition,
  getRecipients, createRecipient, updateRecipient, deleteRecipient, deactivateRecipient,
  getBrands, createBrand, updateBrand, deleteBrand, deactivateBrand,
  getModels, createModel, updateModel, deleteModel, deactivateModel,
  uploadModelImage, deleteModelImage,
  getVendors, createVendor, updateVendor, deleteVendor, deactivateVendor,
  getReminderSchedules, updateReminderSchedule,
} from '../api/client'
import api from '../api/client'
import SwLibraryTab from '../components/comset/SwLibraryTab'
import ChecklistItemsTab from '../components/comset/ChecklistItemsTab'
import CheckTemplatesTab from '../components/comset/CheckTemplatesTab'
import ComsetTab from '../components/comset/ComsetTab'
import StockLocationsTab from '../components/configuration/StockLocationsTab'
import WoCategoriesTab from '../components/configuration/WoCategoriesTab'
import BorrowPolicyTab from '../components/configuration/BorrowPolicyTab'
import ImportExportButtons from '../components/configuration/ImportExportButtons'

// ── ช่อง "Name (EN)" ต้องเป็นอังกฤษเท่านั้น (ไม่บังคับพิมพ์ใหญ่ทั้งหมด — เก็บตัวพิมพ์ตามที่พิมพ์จริง) ──
// เหตุผล: ชื่อ EN มักถูกใช้เป็น label หลักเวลาไม่มี nameTh หรือ fallback ในหลายจุดของระบบ
// ควรคงเป็นอังกฤษล้วนเพื่อความสม่ำเสมอ ต่างจากช่อง nameTh ที่ตั้งใจให้พิมพ์ไทยได้เต็มที่
const filterEnglishOnly = (value: string) => value.replace(/[^a-zA-Z0-9\s\-.,&/()']/g, '')

// ── Helpers ───────────────────────────────────────────────────────────────────

const getCategoriesByDept = (deptCode?: string) =>
  api.get('/configuration/categories', { params: deptCode ? { departmentCode: deptCode } : {} })

const getSubCategoriesByDept = (category?: string, deptCode?: string) =>
  api.get('/configuration/sub-categories', {
    params: { ...(category ? { category } : {}), ...(deptCode ? { departmentCode: deptCode } : {}) },
  })

// ── Generic CRUD table ────────────────────────────────────────────────────────

function CrudTable({ data, onEdit, onDelete, onDeactivate, columns, canDelete = true }: {
  data: any[]
  onEdit: (item: any) => void
  onDelete: (item: any) => void
  onDeactivate: (item: any) => void
  columns: { key: string; label: string }[]
  canDelete?: boolean
}) {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()

  if (isMobile) {
    // ── มือถือ: ออกแบบใหม่ทั้งหมดเป็นการ์ด ไม่ใช่ตารางเดิมย่อขนาดลงมา ──
    // เดิมตารางเว็บถูกอัดลงจอมือถือ ตัดคอลัมน์ ต้องเลื่อนซ้าย-ขวา อ่านยาก กดปุ่มเล็กๆ ผิดง่าย
    // แนวคิดใหม่: การ์ดต่อ 1 รายการ, ข้อมูลหลัก (ชื่อ) เด่นสุด, โค้ดเป็น badge, field อื่นๆ
    // แสดงเป็น label: value เรียงตามธรรมชาติของการอ่านบนมือถือ (บนลงล่าง ไม่ใช่ซ้ายไปขวา),
    // ปุ่มจัดการ (แก้ไข/ปิดใช้งาน/ลบ) ย้ายไปรวมในเมนู ⋮ จุดเดียว แทนไอคอน 2-3 ปุ่มเรียงกันบนแถวแคบๆ
    const codeCol = columns.find(c => c.key === 'code')
    const primaryCol = columns.find(c => c.key !== 'code') ?? columns[0]
    const restCols = columns.filter(c => c.key !== codeCol?.key && c.key !== primaryCol?.key)

    if (data.length === 0) {
      return <Center p="xl"><Text c="dimmed" size="sm">{t('config.noData','No data found')}</Text></Center>
    }

    return (
      <Stack gap="xs" p="xs">
        {data.map((item) => (
          <Card key={item.id} withBorder radius="lg" p="md"
            style={{ opacity: item.isActive === false ? 0.55 : 1 }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
              <Box style={{ minWidth: 0, flex: 1 }}>
                {codeCol && item[codeCol.key] && (
                  <Badge variant="light" color="dark" size="sm" mb={6}
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>
                    {item[codeCol.key]}
                  </Badge>
                )}
                <Text fw={700} size="md" style={{ wordBreak: 'break-word' }}>
                  {item[primaryCol?.key] ?? '-'}
                </Text>
              </Box>
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                <Badge color={item.isActive !== false ? 'lime' : 'gray'} variant="light" size="sm">
                  {item.isActive !== false ? t('config.active','Active') : t('config.inactive','Inactive')}
                </Badge>
                <Menu shadow="md" width={200} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="lg">
                      <IconDotsVertical size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => onEdit(item)}>
                      {t('config.edit','Edit')}
                    </Menu.Item>
                    {canDelete && item.isActive !== false && (
                      <Menu.Item leftSection={<IconBan size={15} />} color="orange" onClick={() => onDeactivate(item)}>
                        {t('config.deactivate','Deactivate')}
                      </Menu.Item>
                    )}
                    {canDelete && (
                      <Menu.Item leftSection={<IconTrash size={15} />} color="red" onClick={() => onDelete(item)}>
                        {t('config.deletePerm','Delete permanently')}
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>

            {restCols.some(c => item[c.key]) && (
              <>
                <Divider my={8} />
                <Stack gap={4}>
                  {restCols.map(c => item[c.key] ? (
                    <Group key={c.key} gap={6} wrap="nowrap" align="flex-start">
                      <Text size="xs" c="dimmed" style={{ flexShrink: 0, minWidth: 90 }}>{c.label}</Text>
                      <Text size="xs" style={{ wordBreak: 'break-word' }}>{item[c.key]}</Text>
                    </Group>
                  ) : null)}
                </Stack>
              </>
            )}
          </Card>
        ))}
      </Stack>
    )
  }

  return (
    <Table highlightOnHover>
      <Table.Thead style={{ background: '#f8f7f4' }}>
        <Table.Tr>
          {columns.map((c) => <Table.Th key={c.key}>{c.label}</Table.Th>)}
          <Table.Th>{t('config.statusHeader','Status')}</Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.length === 0 ? (
          <Table.Tr>
            <Table.Td colSpan={columns.length + 2}>
              <Center p="xl"><Text c="dimmed" size="sm">{t('config.noData','No data found')}</Text></Center>
            </Table.Td>
          </Table.Tr>
        ) : data.map((item) => (
          <Table.Tr key={item.id} style={{ opacity: item.isActive === false ? 0.5 : 1 }}>
            {columns.map((c) => (
              <Table.Td key={c.key}>
                <Text size="sm" fw={c.key === 'code' ? 700 : 400}
                  style={{ fontFamily: c.key === 'code' ? 'Inter, sans-serif' : undefined }}>
                  {item[c.key] ?? '-'}
                </Text>
              </Table.Td>
            ))}
            <Table.Td>
              <Badge color={item.isActive !== false ? 'lime' : 'gray'} variant="light" size="sm">
                {item.isActive !== false ? t('config.active','Active') : t('config.inactive','Inactive')}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Group gap={4}>
                <Tooltip label={t("config.edit","Edit")}>
                  <ActionIcon variant="subtle" color="gray" onClick={() => onEdit(item)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
                {canDelete && item.isActive !== false && (
                  <Tooltip label={t("config.deactivate","Deactivate")}>
                    <ActionIcon variant="subtle" color="orange" onClick={() => onDeactivate(item)}>
                      <IconBan size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip label={t("config.deletePerm","Delete permanently")}>
                    <ActionIcon variant="subtle" color="red" onClick={() => onDelete(item)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody></Table>
  )
}

// ── Form Modal ────────────────────────────────────────────────────────────────

function FormModal({ opened, onClose, onSave, editing, saving, children, title }: any) {
  const { t } = useTranslation()
  return (
    <Modal opened={opened} onClose={onClose}
      title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 16 }}>{title}</Text>}
      size="sm" radius="lg">
      <Stack gap="sm">
        {children}
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>{t("common.cancel","Cancel")}</Button>
          <Button loading={saving} onClick={onSave}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
            {editing ? 'Save Changes' : 'Create'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

// ── Notifications Panel (embedded in Configuration) ──────────────────────────
const PRESET_SLOTS = ['07:00','08:00','09:00','10:00','12:00','13:00','15:00','17:00','18:00','20:00']

interface ReminderSchedule { orderType: string; timeSlots: string[]; staleAfterHours: number; enabled: boolean }

function ReminderCard({ title, color, schedule, t, onSaved }: {
  title: string; color: string; schedule: ReminderSchedule; t: any; onSaved: () => void
}) {
  const [local, setLocal] = React.useState<ReminderSchedule>(schedule)
  const [saving, setSaving] = React.useState(false)
  React.useEffect(() => setLocal(schedule), [schedule])

  const addSlot = (s: string) => { if (!local.timeSlots.includes(s)) setLocal(p => ({ ...p, timeSlots: [...p.timeSlots, s].sort() })) }
  const removeSlot = (s: string) => setLocal(p => ({ ...p, timeSlots: p.timeSlots.filter(x => x !== s) }))

  const save = async () => {
    setSaving(true)
    try {
      await updateReminderSchedule(local.orderType, { timeSlots: local.timeSlots, staleAfterHours: local.staleAfterHours, enabled: local.enabled })
      notifications.show({ color: 'teal', message: t('common.success') })
      onSaved()
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
    finally { setSaving(false) }
  }

  return (
    <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
      <Group justify="space-between" mb="md">
        <Group gap={8}><Box style={{ width: 10, height: 10, borderRadius: '50%', background: color }} /><Text fw={600} size="sm">{title}</Text></Group>
        <Switch checked={local.enabled} onChange={e => setLocal(p => ({ ...p, enabled: e.currentTarget.checked }))}
          label={local.enabled ? t('common.active','ใช้งาน') : t('common.inactive','ปิด')} color="teal" size="sm" />
      </Group>
      <Divider mb="md" />
      {/* Option A */}
      <Stack gap={8} mb="md">
        <Group gap={6}><IconBell size={14} color="#0d9488" /><Text size="xs" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>{t('reminder.optionA','แจ้งเตือนตามเวลาที่กำหนด')}</Text></Group>
        <Text size="xs" c="dimmed">{t('reminder.optionAHint','ส่งสรุปงานค้างเข้า LINE ตามเวลาที่กำหนด — รองรับหลายรอบต่อวัน')}</Text>
        <Group gap={6} wrap="wrap">
          {local.timeSlots.map(s => (
            <Badge key={s} size="sm" variant="light" color="teal"
              rightSection={<ActionIcon size={12} variant="transparent" color="teal" onClick={() => removeSlot(s)}><IconTrashSmall size={10} /></ActionIcon>}>
              🕐 {s}
            </Badge>
          ))}
          {!local.timeSlots.length && <Text size="xs" c="dimmed">{t('reminder.noSlots','ยังไม่มีเวลา')}</Text>}
        </Group>
        <Group gap={4} wrap="wrap">
          {PRESET_SLOTS.map(s => (
            <Button key={s} size="xs" variant={local.timeSlots.includes(s) ? 'filled' : 'outline'} color="teal"
              leftSection={<IconPlusSmall size={10} />} disabled={local.timeSlots.includes(s)}
              onClick={() => addSlot(s)}>{s}</Button>
          ))}
        </Group>
      </Stack>
      <Divider mb="md" />
      {/* Option C */}
      <Stack gap={8} mb="md">
        <Group gap={6}><IconBell size={14} color="#f59e0b" /><Text size="xs" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>{t('reminder.optionC','แจ้งเตือนงานที่ค้างนาน')}</Text></Group>
        <Text size="xs" c="dimmed">{t('reminder.optionCHint','เมื่องานค้างนานเกินกำหนด ระบบแจ้งเตือนซ้ำทุก 1 ชั่วโมง (0 = ปิด)')}</Text>
        <Group align="flex-end" gap="sm">
          <NumberInput label={t('reminder.staleHours','แจ้งเตือนเมื่อค้างนานกว่า (ชม.)')}
            value={local.staleAfterHours} onChange={v => setLocal(p => ({ ...p, staleAfterHours: Number(v) || 0 }))}
            min={0} max={72} step={1} w={200} size="sm"
            description={local.staleAfterHours > 0 ? `แจ้งเตือนซ้ำทุก 1 ชม. เมื่อค้างเกิน ${local.staleAfterHours} ชม.` : 'ปิดการแจ้งเตือนงานค้างนาน'} />
          {local.staleAfterHours > 0 && <Badge color="orange" variant="light">⏰ {'>'}{local.staleAfterHours} ชม.</Badge>}
        </Group>
      </Stack>
      <Group justify="flex-end">
        <Button size="sm" loading={saving} onClick={save}
          style={{ background: 'var(--brand-primary,#0f172a)', color: 'var(--brand-on-primary,#fff)', border: '1px solid var(--brand-border,transparent)' }}>
          {t('common.save','บันทึก')}
        </Button>
      </Group>
    </Box>
  )
}

function NotificationsPanel({ t }: { t: any }) {
  const [loading, setLoading] = React.useState(true)
  const [wo, setWo] = React.useState<ReminderSchedule>({ orderType: 'workorder', timeSlots: ['08:00'], staleAfterHours: 0, enabled: true })
  const [hk, setHk] = React.useState<ReminderSchedule>({ orderType: 'hkorder', timeSlots: ['08:00'], staleAfterHours: 0, enabled: true })

  const load = () => getReminderSchedules().then(({ data }) => {
    if (data.workorder) setWo(data.workorder)
    if (data.hkorder)   setHk(data.hkorder)
  }).catch(() => {}).finally(() => setLoading(false))

  React.useEffect(() => { load() }, [])

  return (
    <Stack gap="lg">
      <Box>
        <Text fw={600} mb={2}>{t('config.notifications','การแจ้งเตือน LINE')}</Text>
        <Text size="xs" c="dimmed">{t('reminder.subtitle','ตั้งเวลาแจ้งเตือนงานค้างเข้ากลุ่ม LINE — รองรับหลายรอบต่อวัน และแจ้งซ้ำเมื่อค้างนาน')}</Text>
      </Box>
      {loading ? <Text size="sm" c="dimmed">{t('common.loading','กำลังโหลด...')}</Text> : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <ReminderCard title="🔧 Work Order" color="#0d9488" schedule={wo} t={t} onSaved={load} />
          <ReminderCard title="🛎️ HK Order"   color="#6d28d9" schedule={hk} t={t} onSaved={load} />
        </SimpleGrid>
      )}
      <Box style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 16px', border: '1px solid #e2e8f0' }}>
        <Text size="xs" fw={600} c="dimmed" mb={6}>📖 {t('reminder.howItWorks','วิธีการทำงาน')}</Text>
        <Stack gap={3}>
          <Text size="xs" c="dimmed">🔔 <b>{t('reminder.optionA','แจ้งเตือนตามเวลาที่กำหนด')}</b> — {t('reminder.howA','ระบบส่งสรุปงานค้างทั้งหมดเข้ากลุ่ม LINE ตามเวลาที่ตั้งไว้ทุกวัน')}</Text>
          <Text size="xs" c="dimmed">⏰ <b>{t('reminder.optionC','แจ้งเตือนงานที่ค้างนาน')}</b> — {t('reminder.howC','ถ้างานใดค้างนานเกินกำหนด ระบบแจ้งเตือนซ้ำทุก 1 ชั่วโมง พร้อมระบุว่าค้างมากี่ชั่วโมง')}</Text>
          <Text size="xs" c="dimmed">✅ {t('reminder.howBoth','Work Order และ HK Order ตั้งค่าแยกกันได้อิสระ')}</Text>
        </Stack>
      </Box>
    </Stack>
  )
}

export default function ConfigurationPage() {
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const { roles, departments: userDepts } = useAuth()
  const canDeleteConfig = roles.includes('ADMIN') || roles.includes('ASSET_MANAGER')
  const isAdmin = roles.includes('ADMIN')
  const userDept = (!roles.includes('ADMIN') && !roles.includes('ASSET_MANAGER')) ? (userDepts?.[0] ?? null) : null

  // ── Global tab state (persists tab when modal closes) ─────────────────────
  const { t, i18n } = useTranslation()
  const [activeTab, setActiveTab] = useState<string>('departments')
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false)

  // ── Departments ────────────────────────────────────────────────────────────
  const [depts, setDepts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deptOpen, { open: openDept, close: closeDept }] = useDisclosure(false)
  const [editingDept, setEditingDept] = useState<any>(null)
  const [deptForm, setDeptForm] = useState({ code: '', name: '', nameTh: '', description: '' })

  // ── Department-scoped view: selected dept for cat/subcat management ─────────
  const [selectedDeptCode, setSelectedDeptCode] = useState<string | null>(null)

  // ── Categories (for selected dept or global) ───────────────────────────────
  const [cats, setCats] = useState<any[]>([])
  const [catsLoading, setCatsLoading] = useState(false)
  const [catOpen, { open: openCat, close: closeCat }] = useDisclosure(false)
  const [editingCat, setEditingCat] = useState<any>(null)
  const [catForm, setCatForm] = useState({ code: '', name: '', nameTh: '', description: '', departmentCode: '' })

  // ── Sub-Categories ─────────────────────────────────────────────────────────
  const [subCats, setSubCats] = useState<any[]>([])
  const [subCatsLoading, setSubCatsLoading] = useState(false)
  const [subCatOpen, { open: openSubCat, close: closeSubCat }] = useDisclosure(false)
  const [editingSubCat, setEditingSubCat] = useState<any>(null)
  const [subCatForm, setSubCatForm] = useState({ code: '', name: '', nameTh: '', category: '', departmentCode: '' })

  // ── Locations ──────────────────────────────────────────────────────────────
  const [locationsList, setLocationsList] = useState<any[]>([])
  const [locationOpen, { open: openLoc, close: closeLoc }] = useDisclosure(false)
  const [editingLocation, setEditingLocation] = useState<any>(null)
  const [locationForm, setLocationForm] = useState({ code: '', name: '', nameTh: '', description: '' })

  // ── Location Import ────────────────────────────────────────────────────────
  const [locImportOpen, { open: openLocImport, close: closeLocImport }] = useDisclosure(false)
  const [locImportFile, setLocImportFile] = useState<File | null>(null)
  const [locImporting, setLocImporting] = useState(false)
  const [locImportResult, setLocImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)

  const [brandImportOpen, { open: openBrandImport, close: closeBrandImport }] = useDisclosure(false)
  const [brandImportFile, setBrandImportFile] = useState<File | null>(null)
  const [brandImporting, setBrandImporting] = useState(false)
  const [brandImportResult, setBrandImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)

  const [vendorImportOpen, { open: openVendorImport, close: closeVendorImport }] = useDisclosure(false)
  const [vendorImportFile, setVendorImportFile] = useState<File | null>(null)
  const [vendorImporting, setVendorImporting] = useState(false)
  const [vendorImportResult, setVendorImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)

  // ── Brands ──────────────────────────────────────────────────────────────────
  const [brands, setBrands] = useState<any[]>([])
  const [brandOpen, { open: openBrand, close: closeBrand }] = useDisclosure(false)
  const [editingBrand, setEditingBrand] = useState<any>(null)
  const [brandForm, setBrandForm] = useState({ code: '', name: '', nameTh: '', description: '' })

  // ── Search states per tab ────────────────────────────────────────────────────
  const [deptSearch, setDeptSearch]     = useState('')
  const [catSearch, setCatSearch]       = useState('')
  const [subCatSearch, setSubCatSearch] = useState('')
  const [subCatCategoryFilter, setSubCatCategoryFilter] = useState<string | null>(null)
  const [locSearch, setLocSearch]       = useState('')
  const [locDeptFilter, setLocDeptFilter] = useState<string | null>(null)
  const [posSearch, setPosSearch]       = useState('')
  const [recSearch, setRecSearch]       = useState('')
  const [recDeptFilter, setRecDeptFilter] = useState<string | null>(null)
  const [brandSearch, setBrandSearch]   = useState('')
  const [vendorSearch, setVendorSearch] = useState('')

  // ── Equipment Models ─────────────────────────────────────────────────────────
  const [models, setModels] = useState<any[]>([])
  const [modelOpen, { open: openModel, close: closeModel }] = useDisclosure(false)
  const [editingModel, setEditingModel] = useState<any>(null)
  const [modelForm, setModelForm] = useState({ code: '', name: '', nameTh: '', brandCode: '', categoryCode: '', subCategoryCode: '', description: '', departmentCode: '' })
  const [modelBrandFilter, setModelBrandFilter] = useState<string | null>(null)
  const [modelCategoryFilter, setModelCategoryFilter] = useState<string | null>(null)
  const [modelSearch, setModelSearch] = useState('')
  const [modelImageFile, setModelImageFile] = useState<File | null>(null)
  const [modelImagePreview, setModelImagePreview] = useState<string | null>(null)
  const [modelImageUploading, setModelImageUploading] = useState(false)

  // ── Positions ──────────────────────────────────────────────────────────────
  const [positions, setPositions] = useState<any[]>([])
  const [posOpen, { open: openPos, close: closePos }] = useDisclosure(false)
  const [editingPos, setEditingPos] = useState<any>(null)
  const [posForm, setPosForm] = useState({ name: '', nameTh: '', description: '' })

  // ── Recipients ─────────────────────────────────────────────────────────────
  const [recipients, setRecipients] = useState<any[]>([])
  const [recOpen, { open: openRec, close: closeRec }] = useDisclosure(false)

  // ── Vendors ────────────────────────────────────────────────────────────────
  const [vendorsList, setVendorsList] = useState<any[]>([])
  const [vendorOpen, { open: openVendor, close: closeVendor }] = useDisclosure(false)
  const [editingVendor, setEditingVendor] = useState<any>(null)
  const [vendorForm, setVendorForm] = useState({ name: '', nameTh: '', contactName: '', phone: '', email: '', address: '' })
  const [editingRec, setEditingRec] = useState<any>(null)
  const [recForm, setRecForm] = useState({ name: '', nameTh: '', position: '', email: '', department: '', location: '', phone: '' })

  // ── Load departments ───────────────────────────────────────────────────────

  const loadDepts = async () => {
    setLoading(true)
    const d = await getDepartments()
    setDepts(d.data)
    setLoading(false)
  }

  useEffect(() => { loadDepts(); loadLocations(); loadPositions() }, [])

  // ── Load categories & subcategories when dept changes ─────────────────────

  const loadCats = async (deptCode?: string | null) => {
    setCatsLoading(true)
    const res = await getCategoriesByDept(deptCode ?? undefined)
    setCats(res.data)
    setCatsLoading(false)
  }

  const loadSubCats = async (deptCode?: string | null) => {
    setSubCatsLoading(true)
    const res = await getSubCategoriesByDept(undefined, deptCode ?? undefined)
    setSubCats(res.data)
    setSubCatsLoading(false)
  }

  const loadLocations = async () => {
    const res = await getLocations()
    setLocationsList(res.data)
  }

  const loadPositions = async () => {
    const res = await getPositions()
    setPositions(res.data)
  }

  const loadBrands = async () => {
    const res = await getBrands()
    setBrands(res.data)
  }

  const loadModels = async (brandCode?: string) => {
    // Always fetch all models, filter in UI for reliability
    const res = await getModels(brandCode)
    setModels(res.data)
  }

  // Always reload models when tab is opened (fix: don't skip when filter is null)
  useEffect(() => {
    if (activeTab === 'models') {
      loadModels()
      if (brands.length === 0) loadBrands()
      if (cats.length === 0) loadCats()
      if (subCats.length === 0) loadSubCats()   // need all subCats for Product sub-category dropdown
    }
  }, [activeTab])

  const loadRecipients = async () => {
    const res = await getRecipients()
    setRecipients(res.data)
  }

  const loadVendors = async () => {
    const res = await getVendors()
    setVendorsList(res.data)
  }

  useEffect(() => {
    if (activeTab === 'categories') loadCats(selectedDeptCode)
    if (activeTab === 'subcategories') { loadSubCats(selectedDeptCode); if (cats.length === 0) loadCats() }
    if (activeTab === 'locations') loadLocations()
    if (activeTab === 'positions') loadPositions()
    if (activeTab === 'recipients') { loadRecipients(); if (positions.length === 0) loadPositions() }
    if (activeTab === 'brands') loadBrands()
    if (activeTab === 'vendors') loadVendors()
  }, [activeTab, selectedDeptCode])

  // ── Delete / Deactivate helpers ────────────────────────────────────────────

  const handleDelete = (label: string, deleteFn: () => Promise<any>, reloadFn: () => void) => {
    modals.openConfirmModal({
      title: t('config.deleteTitle', 'Delete {{label}}', { label }),
      children: (
        <Stack gap="xs">
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('config.deletePermanentBody', 'Permanently deleted. Blocked if used by any asset.')}
          </Alert>
          <Text size="sm">{t('config.useDeactivateInstead', 'Use Deactivate to hide without deleting.')}</Text>
        </Stack>
      ),
      labels: { confirm: t('common.delete', 'Delete'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteFn()
          notifications.show({ color: 'lime', message: t('config.deletedSuccessfully', 'Deleted successfully') })
          reloadFn()
        } catch (e: any) {
          notifications.show({ color: 'orange', title: t('config.cannotDelete', 'Cannot Delete'), message: e.response?.data?.message ?? t('config.deleteFailed', 'Delete failed') })
        }
      },
    })
  }

  const confirmDeactivate = (label: string, fn: () => Promise<any>, reloadFn: () => void) => {
    modals.openConfirmModal({
      title: t('config.deactivateTitle', 'Deactivate {{label}}', { label }),
      children: <Text size="sm">{t('config.hiddenNotDeleted', 'Hidden from dropdowns but not deleted.')}</Text>,
      labels: { confirm: t('common.deactivate', 'Deactivate'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        await fn()
        notifications.show({ color: 'lime', message: 'Deactivated' })
        reloadFn()
      },
    })
  }

  // ── Department CRUD ────────────────────────────────────────────────────────

  const saveDept = async () => {
    if (!deptForm.code || !deptForm.name) {
      notifications.show({ color: 'red', message: 'Code and Name are required' }); return
    }
    setSaving(true)
    try {
      if (editingDept) await updateDepartment(editingDept.id, deptForm)
      else await createDepartment(deptForm)
      notifications.show({ color: 'lime', message: 'Saved' })
      closeDept()
      loadDepts()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  // ── Category CRUD ──────────────────────────────────────────────────────────

  const saveCat = async () => {
    if (!catForm.code || !catForm.name) {
      notifications.show({ color: 'red', message: 'Code and Name are required' }); return
    }
    setSaving(true)
    try {
      if (editingCat) await updateCategory(editingCat.id, catForm)
      else await createCategory(catForm)
      notifications.show({ color: 'lime', message: 'Saved' })
      closeCat()
      // ✅ Stay on categories tab
      loadCats(selectedDeptCode)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  // ── SubCategory CRUD ───────────────────────────────────────────────────────

  const saveLocation = async () => {
    if (!locationForm.code || !locationForm.name) {
      notifications.show({ color: 'red', message: 'Code and Name are required' }); return
    }
    setSaving(true)
    try {
      if (editingLocation) {
        await updateLocation(editingLocation.id, locationForm)
        notifications.show({ color: 'lime', message: 'Location updated' })
      } else {
        await createLocation({ ...locationForm })
        notifications.show({ color: 'lime', message: 'Location created' })
      }
      closeLoc(); loadLocations()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  const savePosition = async () => {
    if (!posForm.name) { notifications.show({ color: 'red', message: 'Name is required' }); return }
    setSaving(true)
    try {
      if (editingPos) {
        await updatePosition(editingPos.id, posForm)
        notifications.show({ color: 'lime', message: 'Position updated' })
      } else {
        await createPosition(posForm)
        notifications.show({ color: 'lime', message: 'Position created' })
      }
      closePos(); loadPositions()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  const saveRecipient = async () => {
    if (!recForm.name) { notifications.show({ color: 'red', message: 'Name is required' }); return }
    setSaving(true)
    try {
      if (editingRec) {
        await updateRecipient(editingRec.id, recForm)
        notifications.show({ color: 'lime', message: 'Recipient updated' })
      } else {
        await createRecipient(recForm)
        notifications.show({ color: 'lime', message: 'Recipient created' })
      }
      closeRec(); loadRecipients()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  const saveVendor = async () => {
    if (!vendorForm.name) { notifications.show({ color: 'red', message: 'Vendor name required' }); return }
    setSaving(true)
    try {
      if (editingVendor) { await updateVendor(editingVendor.id, vendorForm); notifications.show({ color: 'lime', message: 'Vendor updated' }) }
      else { await createVendor(vendorForm); notifications.show({ color: 'lime', message: 'Vendor created' }) }
      closeVendor(); loadVendors()
    } catch (e: any) { notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' }) }
    finally { setSaving(false) }
  }

  const saveBrand = async () => {
    if (!brandForm.code || !brandForm.name) { notifications.show({ color: 'red', message: 'Code and Name required' }); return }
    setSaving(true)
    try {
      if (editingBrand) { await updateBrand(editingBrand.id, brandForm); notifications.show({ color: 'lime', message: 'Brand updated' }) }
      else { await createBrand(brandForm); notifications.show({ color: 'lime', message: 'Brand created' }) }
      closeBrand(); loadBrands()
    } catch (e: any) { notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' }) }
    finally { setSaving(false) }
  }

  const saveModel = async () => {
    if (!modelForm.code || !modelForm.name) { notifications.show({ color: 'red', message: t('config.codeNameRequired', 'Code and Name required') }); return }
    setSaving(true)
    try {
      let saved: any
      if (editingModel) {
        const res = await updateModel(editingModel.id, modelForm)
        saved = res.data
        notifications.show({ color: 'lime', message: t('config.modelUpdated', 'Model updated') })
      } else {
        const res = await createModel(modelForm)
        saved = res.data
        notifications.show({ color: 'lime', message: t('config.modelCreated', 'Model created') })
      }
      // Upload new image if selected
      if (modelImageFile && saved?.id) {
        setModelImageUploading(true)
        try { await uploadModelImage(saved.id, modelImageFile) }
        catch (_) { notifications.show({ color: 'orange', message: t('config.modelImageUploadFailed', 'Model saved but image upload failed') }) }
        finally { setModelImageUploading(false) }
      }
      closeModel(); loadModels()
    } catch (e: any) { notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'Error') }) }
    finally { setSaving(false) }
  }

  const handleLocImport = async () => {
    if (!locImportFile) return
    setLocImporting(true)
    try {
      const form = new FormData()
      form.append('file', locImportFile)
      const res = await api.post('/configuration/locations/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      setLocImportResult(res.data)
      if (res.data.imported > 0) loadLocations()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Import failed' })
    } finally { setLocImporting(false) }
  }

  const downloadLocationTemplate = () => {
    const headers = ['code', 'name', 'departmentCode', 'description']
    const example = ['OFFICE-A-F1', 'Office A - Floor 1', 'IT', 'Main IT workspace']
    const csv = [headers.join(','), example.join(',')].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'location_import_template.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const handleBrandImport = async () => {
    if (!brandImportFile) return
    setBrandImporting(true)
    try {
      const form = new FormData()
      form.append('file', brandImportFile)
      const res = await api.post('/configuration/brands/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      setBrandImportResult(res.data)
      if (res.data.imported > 0) loadBrands()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Import failed' })
    } finally { setBrandImporting(false) }
  }

  const downloadBrandTemplate = () => {
    const headers = ['code', 'name', 'nameTh', 'description', 'departmentCode']
    const example = ['DELL', 'Dell', 'เดลล์', 'Laptops & desktops', '']
    const csv = [headers.join(','), example.join(',')].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'brand_import_template.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const handleVendorImport = async () => {
    if (!vendorImportFile) return
    setVendorImporting(true)
    try {
      const form = new FormData()
      form.append('file', vendorImportFile)
      const res = await api.post('/configuration/vendors/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      setVendorImportResult(res.data)
      if (res.data.imported > 0) loadVendors()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Import failed' })
    } finally { setVendorImporting(false) }
  }

  const downloadVendorTemplate = () => {
    const headers = ['name', 'contactName', 'phone', 'email', 'address']
    const example = ['ABC Supplies Co., Ltd.', 'Somchai', '081-234-5678', 'sales@abc.com', '123 Bangkok']
    const csv = [headers.join(','), example.join(',')].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'vendor_import_template.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const saveSubCat = async () => {
    if (!subCatForm.code || !subCatForm.name) {
      notifications.show({ color: 'red', message: 'Code and Name are required' }); return
    }
    setSaving(true)
    try {
      if (editingSubCat) await updateSubCategory(editingSubCat.id, subCatForm)
      else await createSubCategory(subCatForm)
      notifications.show({ color: 'lime', message: 'Saved' })
      closeSubCat()
      // ✅ Stay on subcategories tab
      loadSubCats(selectedDeptCode)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  // ── Department selector options ────────────────────────────────────────────

  const deptOptions = [
    { value: '__global__', label: '🌐 Global (All Departments)' },
    ...depts.filter((d) => d.isActive !== false).map((d) => ({
      value: d.code,
      label: `${d.name}  (${d.code})`,
    })),
  ]

  const effectiveDeptCode = selectedDeptCode === '__global__' ? null : selectedDeptCode

  // ── Render ─────────────────────────────────────────────────────────────────

  // ── Side Nav groups ──────────────────────────────────────────────────────────
  const navGroups = [
    {
      key: 'org', label: t('config.groupOrg','องค์กร'), icon: <IconBuilding size={15} />, color: '#0ea5e9',
      hint: null,
      items: [
        { value: 'departments', label: t('config.departments','Departments'), icon: <IconBuilding size={14} />, count: depts.length },
        { value: 'locations',   label: t('config.locations','Locations'),   icon: <IconMapPin size={14} /> },
      ],
    },
    {
      key: 'assets', label: t('config.groupAssets','สินทรัพย์'), icon: <IconBox size={15} />, color: '#f59e0b',
      hint: null,
      items: [
        { value: 'categories',   label: t('config.categories','Categories'),     icon: <IconCategory size={14} /> },
        { value: 'subcategories',label: t('config.subCategories','Sub-Categories'), icon: <IconTag size={14} /> },
        { value: 'brands',       label: t('config.brands','Brands'),         icon: <IconDeviceLaptop size={14} /> },
        { value: 'models',       label: t('config.products','Products'),       icon: <IconVersions size={14} /> },
        { value: 'vendors',      label: t('config.vendors','Vendors'),        icon: <IconTruck size={14} /> },
      ],
    },
    {
      key: 'people', label: t('config.groupPeople','บุคคล'), icon: <IconUsersGroup size={15} />, color: '#8b5cf6',
      hint: null,
      items: [
        { value: 'positions',  label: t('config.positions','Positions'),  icon: <IconBriefcase size={14} /> },
        { value: 'wo-categories', label: 'ประเภทงาน (WO/HKO)', icon: <IconCategory size={14} /> },
        { value: 'recipients', label: t('config.recipients','Recipients'), icon: <IconUser size={14} /> },
      ],
    },
    {
      key: 'comset', label: t('config.groupComset','COMSET & Checking'), icon: <IconListCheck size={15} />, color: '#10b981',
      hint: null,
      items: [
        { value: 'sw-library',   label: t('config.swLibrary','SW Library'),        icon: <IconCode size={14} /> },
        { value: 'chk-items',    label: t('config.checklistItems','Checklist Items'),   icon: <IconListCheck size={14} /> },
        { value: 'chk-templates',label: t('config.checkTemplates','Check Templates'),   icon: <IconTemplate size={14} /> },
        { value: 'comsets',      label: t('config.comset','COMSET'),            icon: <IconServer size={14} /> },
      ],
    },
    {
      key: 'system', label: t('config.groupSystem','ระบบ'), icon: <IconBell size={15} />, color: '#f59e0b',
      hint: null,
      items: [
        { value: 'notifications', label: t('config.notifications','การแจ้งเตือน LINE'), icon: <IconBell size={14} /> },
        { value: 'stock-locations', label: t('config.stockLocations','คลังสต็อก'), icon: <IconBuildingWarehouse size={14} /> },
        { value: 'borrow-policy', label: t('config.borrowPolicy','Borrow Policy'), icon: <IconClock size={14} /> },
      ],
    },
  ]

  // ── Accordion open state: default open group containing active tab ────────────
  const defaultOpenKey = navGroups.find(g => g.items.some(i => i.value === 'departments'))?.key ?? ''
  const [openGroups, setOpenGroups] = useState<string[]>(['org', 'assets', 'people', 'comset', 'system'])

  const toggleGroup = (label: string) => {
    setOpenGroups(prev => prev.includes(label) ? prev.filter(g => g !== label) : [...prev, label])
  }

  // Auto-open group when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value)
    const group = navGroups.find(g => g.items.some(i => i.value === value))
    if (group && !openGroups.includes(group.key)) {
      setOpenGroups(prev => [...prev, group.key])
    }
  }

  const NavItem = ({ item }: { item: any }) => {
    const isActive = activeTab === item.value
    return (
      <Box onClick={() => handleTabChange(item.value)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 14px 7px 28px', borderRadius: 8, cursor: 'pointer',
          background: isActive ? '#dcfce7' : 'transparent',
          color: isActive ? '#0d9488' : '#475569',
          fontWeight: isActive ? 600 : 400,
          fontSize: 13,
          transition: 'all 0.12s',
          borderLeft: isActive ? '3px solid #0d9488' : '3px solid transparent',
        }}>
        <span style={{ color: isActive ? '#0d9488' : '#94a3b8' }}>{item.icon}</span>
        <span style={{ flex: 1 }}>{item.label}</span>
        {item.count !== undefined && (
          <Badge size="xs" color={isActive ? 'teal' : 'gray'} variant="light">{item.count}</Badge>
        )}
      </Box>
    )
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      {/* Header */}
      <Box mb="xl">
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
          {t('config.title','Configuration')}
        </Text>
        <Text size="sm" c="dimmed">{t('config.subtitle','จัดการข้อมูลหลักของระบบ AssetOS')}</Text>
      </Box>

      {loading ? <Center p="xl"><Loader color="teal" /></Center> : (
        <Box style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

          {/* ── Side Navigation — Accordion ── */}
          {!isSmall && (
            <Box style={{ width: 220, flexShrink: 0, position: 'sticky', top: 24, background: '#f1f5f9', borderRadius: 14, padding: '8px 6px', border: '1px solid #e2e8f0' }}>
              <Stack gap={2}>
                {navGroups.map(group => {
                  const isOpen = openGroups.includes(group.key)
                  const hasActive = group.items.some(i => i.value === activeTab)
                  return (
                    <Box key={group.key}
                      style={{ borderRadius: 10, overflow: 'hidden', border: hasActive ? `1px solid ${group.color}22` : '1px solid transparent', marginBottom: 4 }}>
                      {/* Group Header — clickable */}
                      <Box onClick={() => toggleGroup(group.key)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '9px 12px', cursor: 'pointer', borderRadius: 10,
                          background: hasActive ? `${group.color}18` : isOpen ? '#e8edf2' : 'transparent',
                          transition: 'background 0.15s',
                        }}>
                        <span style={{ color: group.color }}>{group.icon}</span>
                        <Text size="xs" fw={700} style={{ flex: 1, color: hasActive ? group.color : '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {group.label}
                        </Text>
                        <IconChevronDown size={13} color="#94a3b8"
                          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                      </Box>
                      {/* Items — shown when open */}
                      {isOpen && (
                        <Box style={{ paddingBottom: 4 }}>
                          {group.hint && (
                            <Text size="xs" c="dimmed" style={{ padding: '2px 12px 6px 28px', lineHeight: 1.4, borderLeft: `3px solid ${group.color}44`, marginLeft: 12, marginRight: 8, background: '#fafafa', borderRadius: 4, marginBottom: 4 }}>
                              {group.hint}
                            </Text>
                          )}
                          {group.items.map(item => <NavItem key={item.value} item={item} />)}
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Stack>
            </Box>
          )}

          {/* Tablet: icon-only collapsed nav */}
          {isTablet && !isMobile && (
            <Box style={{ width: 52, flexShrink: 0, position: 'sticky', top: 24, background: '#f1f5f9', borderRadius: 14, padding: '8px 4px', border: '1px solid #e2e8f0' }}>
              <Stack gap={2} align="center">
                {navGroups.map(group => {
                  const hasActive = group.items.some(i => i.value === activeTab)
                  return (
                    <Box key={group.key}>
                      <Box style={{ width: 1, height: 6 }} />
                      {group.items.map(item => (
                        <Tooltip key={item.value} label={item.label} position="right" withArrow>
                          <Box onClick={() => handleTabChange(item.value)}
                            style={{
                              width: 36, height: 36, borderRadius: 8, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: activeTab === item.value ? '#dcfce7' : 'transparent',
                              color: activeTab === item.value ? '#0d9488' : '#94a3b8',
                              transition: 'all 0.12s',
                              marginBottom: 2,
                            }}>
                            {item.icon}
                          </Box>
                        </Tooltip>
                      ))}
                      {hasActive && <Box style={{ width: 24, height: 2, background: group.color, borderRadius: 2, margin: '2px auto' }} />}
                    </Box>
                  )
                })}
              </Stack>
            </Box>
          )}

          {/* Mobile: floating menu button + Drawer */}
          {isMobile && (
            <>
              <Box style={{ position: 'fixed', bottom: 80, right: 16, zIndex: 200 }}>
                <ActionIcon size={48} radius="xl" color="teal" variant="filled"
                  onClick={openDrawer}
                  style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                  <IconMenu2 size={22} />
                </ActionIcon>
              </Box>
              <Drawer opened={drawerOpened} onClose={closeDrawer} position="bottom"
                size="75%" radius="lg" title={
                  <Text fw={700} size="sm">เมนู Configuration</Text>
                }>
                <ScrollArea h="100%">
                  <Stack gap={2} p={4}>
                    {navGroups.map(group => {
                      const isOpen = openGroups.includes(group.key)
                      const hasActive = group.items.some(i => i.value === activeTab)
                      return (
                        <Box key={group.label} mb={4} style={{ borderRadius: 10, overflow: 'hidden', border: hasActive ? `1px solid ${group.color}33` : '1px solid #e2e8f0' }}>
                          <Box onClick={() => toggleGroup(group.key)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer', background: hasActive ? `${group.color}12` : '#f8fafc' }}>
                            <span style={{ color: group.color }}>{group.icon}</span>
                            <Text size="sm" fw={700} style={{ flex: 1, color: hasActive ? group.color : '#374151' }}>{group.label}</Text>
                            <IconChevronDown size={14} color="#94a3b8" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                          </Box>
                          {isOpen && (
                            <Box pb={4}>
                              {group.items.map(item => (
                                <Box key={item.value}
                                  onClick={() => { handleTabChange(item.value); closeDrawer() }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 14px 10px 28px', cursor: 'pointer',
                                    background: activeTab === item.value ? '#dcfce7' : 'transparent',
                                    color: activeTab === item.value ? '#0d9488' : '#475569',
                                    fontWeight: activeTab === item.value ? 600 : 400,
                                    fontSize: 14,
                                    borderLeft: activeTab === item.value ? '3px solid #0d9488' : '3px solid transparent',
                                  }}>
                                  <span style={{ color: activeTab === item.value ? '#0d9488' : '#94a3b8' }}>{item.icon}</span>
                                  {item.label}
                                  {item.count !== undefined && <Badge size="xs" color="teal" variant="light" ml="auto">{item.count}</Badge>}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </Stack>
                </ScrollArea>
              </Drawer>
            </>
          )}

          {/* ── Content Area ── */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            {/* Mobile: show current section name */}
            {isMobile && (() => {
              const current = navGroups.flatMap(g => g.items).find(i => i.value === activeTab)
              const group = navGroups.find(g => g.items.some(i => i.value === activeTab))
              return current ? (
                <Group gap={8} mb="md" style={{ background: '#f1f5f9', borderRadius: 10, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
                  <span style={{ color: group?.color }}>{group?.icon}</span>
                  <Text size="sm" fw={600} c="dark">{current.label}</Text>
                  <ActionIcon size="sm" variant="subtle" color="gray" ml="auto" onClick={openDrawer}>
                    <IconChevronDown size={14} />
                  </ActionIcon>
                </Group>
              ) : null
            })()}
        <Tabs value={activeTab} onChange={(v) => handleTabChange(v ?? 'departments')}>

          {/* ══ DEPARTMENTS ══ */}
          <Tabs.Panel value="departments">
            <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
              <Group gap="sm" />
              <Group gap="sm">
                <TextInput
                  placeholder={t("config.searchCodeOrName","Search code or name...")}
                  leftSection={<IconSearch size={14} />}
                  value={deptSearch}
                  onChange={e => setDeptSearch(e.target.value)}
                  size="sm"
                  style={{ width: 220 }}
                  styles={{ input: { borderColor: '#e2e8f0' } }}
                />
                <Button size="sm" leftSection={<IconPlus size={16} />}
                  onClick={() => { setEditingDept(null); setDeptForm({ code: '', name: '', nameTh: '', description: '' }); openDept() }}
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                  {t('config.addDept','Add Department')}
                </Button>
                <ImportExportButtons resource="departments" onImported={loadDepts} />
              </Group>
            </Group>
            <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'auto' }}>
              <CrudTable
                data={(() => { const q = deptSearch.toLowerCase(); return q ? depts.filter((d: any) => d.code?.toLowerCase().includes(q) || d.name?.toLowerCase().includes(q)) : depts })()}
                columns={[
                  { key: 'code', label: t('config.colCode','Code') },
                  { key: 'name', label: t('config.colName','Name') },
                  { key: 'nameTh', label: t('config.colNameTh','ชื่อภาษาไทย') },
                  { key: 'description', label: t('config.colDescription','Description') },
                ]}
                onEdit={(d) => {
                  setEditingDept(d)
                  setDeptForm({ code: d.code, name: d.name, nameTh: d.nameTh ?? '', description: d.description ?? '' })
                  openDept()
                }}
                onDelete={(d) => handleDelete('Department', () => deleteDepartment(d.id), loadDepts)}
                onDeactivate={(d) => confirmDeactivate('Department', () => deactivateDepartment(d.id), loadDepts)} canDelete={canDeleteConfig}
              />
            </Box>
          </Tabs.Panel>

          {/* ══ CATEGORIES ══ */}
          <Tabs.Panel value="categories">
            {/* Department filter bar */}
            <Paper withBorder p="md" radius="lg" mb="md" style={{ background: '#f8f7f4' }}>
              <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                <Box style={{ flex: 1, maxWidth: 340 }}>
                  <Text size="xs" fw={600} c="dimmed" mb={4} style={{ textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    {t('config.managingCategoriesFor','Managing categories for')}
                  </Text>
                  <Select
                    placeholder={t('config.selectDepartmentPlaceholder','Select department...')}
                    data={deptOptions}
                    value={selectedDeptCode ?? '__global__'}
                    onChange={(v) => setSelectedDeptCode(v === '__global__' ? null : v)}
                    clearable={false}
                  />
                </Box>
                <Group gap="sm">
                  <TextInput
                    placeholder={t("config.searchCodeOrName","Search code or name...")}
                    leftSection={<IconSearch size={14} />}
                    value={catSearch}
                    onChange={e => setCatSearch(e.target.value)}
                    style={{ width: 220 }}
                    styles={{ input: { borderColor: '#e2e8f0' } }}
                  />
                  <Button leftSection={<IconPlus size={16} />}
                    onClick={() => {
                      setEditingCat(null)
                      setCatForm({
                        code: '', name: '', nameTh: '', description: '',
                        departmentCode: effectiveDeptCode ?? '',
                      })
                      openCat()
                    }}
                    style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                    {t('config.addCategory','Add Category')}
                  </Button>
                  <ImportExportButtons resource="categories" onImported={() => loadCats(selectedDeptCode)} />
                </Group>
              </Group>

              {selectedDeptCode && selectedDeptCode !== '__global__' && (
                <Text size="xs" c="dimmed" mt={8}>
                  Showing categories for <b>{depts.find(d => d.code === selectedDeptCode)?.name}</b> + global categories
                </Text>
              )}
            </Paper>

            <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'auto' }}>
              {catsLoading ? <Center p="xl"><Loader color="teal" size="sm" /></Center> : (() => {
                const q = catSearch.toLowerCase()
                const filtered = q ? cats.filter(c => c.code?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q)) : cats
                return (
                  <CrudTable
                    data={filtered}
                    columns={[
                      { key: 'code', label: t('config.colCode','Code') },
                      { key: 'name', label: t('config.colName','Name') },
                      { key: 'nameTh', label: t('config.colNameTh','ชื่อภาษาไทย') },
                      { key: 'description', label: t('config.colDescription','Description') },
                      { key: 'departmentCode', label: t('config.colDepartment','Department') },
                    ]}
                    onEdit={(c) => {
                      setEditingCat(c)
                      setCatForm({
                        code: c.code, name: c.name, nameTh: c.nameTh ?? '',
                        description: c.description ?? '',
                        departmentCode: c.departmentCode ?? '',
                      })
                      openCat()
                    }}
                    onDelete={(c) => handleDelete('Category', () => deleteCategory(c.id), () => loadCats(effectiveDeptCode))}
                    onDeactivate={(c) => confirmDeactivate('Category', () => deactivateCategory(c.id), () => loadCats(effectiveDeptCode))} canDelete={canDeleteConfig}
                  />
                )
              })()}
            </Box>
          </Tabs.Panel>

          {/* ══ SUB-CATEGORIES ══ */}
          <Tabs.Panel value="subcategories">
            {/* Department filter bar */}
            <Paper withBorder p="md" radius="lg" mb="md" style={{ background: '#f8f7f4' }}>
              <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                <Group align="flex-end" gap="md">
                  <Box style={{ flex: 1, maxWidth: 340 }}>
                    <Text size="xs" fw={600} c="dimmed" mb={4} style={{ textTransform: 'uppercase', letterSpacing: '.5px' }}>
                      {t('config.managingSubCategoriesFor','Managing sub-categories for')}
                    </Text>
                    <Select
                      placeholder={t('config.selectDepartmentPlaceholder','Select department...')}
                      data={deptOptions}
                      value={selectedDeptCode ?? '__global__'}
                      onChange={(v) => setSelectedDeptCode(v === '__global__' ? null : v)}
                      clearable={false}
                    />
                  </Box>
                  <Select
                    placeholder={t('config.allCategoriesPlaceholder','All categories')}
                    data={[
                      { value: '', label: t('config.allCategoriesLabel','— All Categories —') },
                      ...cats.map(c => ({ value: c.code, label: `${localizedName(c, i18n.language)} (${c.code})` }))
                    ]}
                    value={subCatCategoryFilter ?? ''}
                    onChange={v => setSubCatCategoryFilter(v || null)}
                    clearable={false}
                    style={{ width: 200 }}
                  />
                </Group>
                <Group gap="sm">
                  <TextInput
                    placeholder={t("config.searchCodeOrName","Search code or name...")}
                    leftSection={<IconSearch size={14} />}
                    value={subCatSearch}
                    onChange={e => setSubCatSearch(e.target.value)}
                    style={{ width: 200 }}
                    styles={{ input: { borderColor: '#e2e8f0' } }}
                  />
                  <Button leftSection={<IconPlus size={16} />}
                    onClick={() => {
                      setEditingSubCat(null)
                      setSubCatForm({
                        code: '', name: '', nameTh: '', category: '',
                        departmentCode: effectiveDeptCode ?? '',
                      })
                      openSubCat()
                    }}
                    style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                    {t('config.addSubCategory','Add Sub-Category')}
                  </Button>
                  <ImportExportButtons resource="sub-categories" onImported={() => loadSubCats(selectedDeptCode)} />
                </Group>
              </Group>
            </Paper>

            <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'auto' }}>
              {subCatsLoading ? <Center p="xl"><Loader color="teal" size="sm" /></Center> : (() => {
                const q = subCatSearch.toLowerCase()
                const filtered = subCats.filter(s => {
                  const matchSearch = !q || s.code?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q) || s.category?.toLowerCase().includes(q)
                  const matchCat = !subCatCategoryFilter || s.category === subCatCategoryFilter
                  return matchSearch && matchCat
                })
                return (
                  <CrudTable
                    data={filtered}
                    columns={[
                      { key: 'code', label: t('config.colCode','Code') },
                      { key: 'name', label: t('config.colName','Name') },
                      { key: 'nameTh', label: t('config.colNameTh','ชื่อภาษาไทย') },
                      { key: 'category', label: t('config.colParentCat','Parent Category') },
                      { key: 'departmentCode', label: t('config.colDepartment','Department') },
                    ]}
                    onEdit={(s) => {
                      setEditingSubCat(s)
                      setSubCatForm({
                        code: s.code, name: s.name, nameTh: s.nameTh ?? '',
                        category: s.category ?? '',
                        departmentCode: s.departmentCode ?? '',
                      })
                      openSubCat()
                    }}
                    onDelete={(s) => handleDelete('Sub-Category', () => deleteSubCategory(s.id), () => loadSubCats(effectiveDeptCode))}
                    onDeactivate={(s) => confirmDeactivate('Sub-Category', () => deactivateSubCategory(s.id), () => loadSubCats(effectiveDeptCode))} canDelete={canDeleteConfig}
                  />
                )
              })()}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="locations">
            <Group justify="space-between" mb="md">
              <Group gap="sm" />
              <Group gap="sm">
                <TextInput
                  placeholder={t("config.searchCodeOrName","Search code or name...")}
                  leftSection={<IconSearch size={14} />}
                  value={locSearch}
                  onChange={e => setLocSearch(e.target.value)}
                  size="sm"
                  style={{ width: 220 }}
                  styles={{ input: { borderColor: '#e2e8f0' } }}
                />
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  onClick={() => { setEditingLocation(null); setLocationForm({ code: '', name: '', nameTh: '', description: '' }); openLoc() }}
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                  {t('config.addLocation','Add Location')}
                </Button>
                <ImportExportButtons resource="locations" onImported={loadLocations} />
              </Group>
            </Group>
            <CrudTable
              data={(() => {
                const q = locSearch.toLowerCase()
                return q ? locationsList.filter(l => l.code?.toLowerCase().includes(q) || l.name?.toLowerCase().includes(q)) : locationsList
              })()}
              columns={[
                { key: 'code', label: t('config.colCode','Code') },
                { key: 'name', label: t('config.colLocationName','Location Name') },
                { key: 'nameTh', label: t('config.colNameTh','ชื่อภาษาไทย') },
                { key: 'description', label: t('config.colDescription','Description') },
              ]}
              onEdit={(l) => { setEditingLocation(l); setLocationForm({ code: l.code, name: l.name, nameTh: l.nameTh ?? '', description: l.description ?? '' }); openLoc() }}
              onDelete={(l) => handleDelete('Location', () => deleteLocation(l.id), loadLocations)}
              onDeactivate={(l) => confirmDeactivate('Location', () => deactivateLocation(l.id), loadLocations)} canDelete={canDeleteConfig}
            />
          </Tabs.Panel>

          {/* ── POSITIONS TAB ── */}
          <Tabs.Panel value="wo-categories">
            <WoCategoriesTab />
          </Tabs.Panel>

          <Tabs.Panel value="positions">
            <Group justify="space-between" mb="md">
              <Group gap="sm" />
              <Group gap="sm">
                <TextInput
                  placeholder={t("config.searchNamePlaceholder","Search name...")}
                  leftSection={<IconSearch size={14} />}
                  value={posSearch}
                  onChange={e => setPosSearch(e.target.value)}
                  size="sm"
                  style={{ width: 200 }}
                  styles={{ input: { borderColor: '#e2e8f0' } }}
                />
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  onClick={() => { setEditingPos(null); setPosForm({ name: '', nameTh: '', description: '' }); openPos() }}
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                  {t('config.addPosition','Add Position')}
                </Button>
                <ImportExportButtons resource="positions" onImported={loadPositions} />
              </Group>
            </Group>
            <CrudTable
              data={(() => { const q = posSearch.toLowerCase(); const filtered = q ? positions.filter(p => p.name?.toLowerCase().includes(q)) : positions; return filtered.map((p: any) => ({ ...p, displayName: localizedNameWithEn(p, i18n.language) })) })()}
              columns={[{ key: 'displayName', label: t('config.colPositionName','Position Name') }, { key: 'description', label: t('config.colDescription','Description') }]}
              onEdit={(p) => { setEditingPos(p); setPosForm({ name: p.name, nameTh: p.nameTh ?? '', description: p.description ?? '' }); openPos() }}
              onDelete={(p) => handleDelete('Position', () => deletePosition(p.id), loadPositions)}
              onDeactivate={(p) => confirmDeactivate('Position', () => deactivatePosition(p.id), loadPositions)} canDelete={canDeleteConfig}
            />
          </Tabs.Panel>

          {/* ── RECIPIENTS TAB ── */}
          <Tabs.Panel value="recipients">
            <Group justify="space-between" mb="md">
              <Group gap="sm">
                <Select
                  placeholder={t("common.allDept","All departments")}
                  clearable searchable
                  value={recDeptFilter}
                  onChange={setRecDeptFilter}
                  data={[...new Set(recipients.map((r: any) => r.department).filter(Boolean))].map((d: any) => ({ value: d, label: d }))}
                  size="sm"
                  style={{ width: 180 }}
                  styles={{ input: { borderColor: '#e2e8f0' } }}
                />
              </Group>
              <Group gap="sm">
                <TextInput
                  placeholder={t("config.searchNameEmailDept","Search name, email, dept...")}
                  leftSection={<IconSearch size={14} />}
                  value={recSearch}
                  onChange={e => setRecSearch(e.target.value)}
                  size="sm"
                  style={{ width: 200 }}
                  styles={{ input: { borderColor: '#e2e8f0' } }}
                />
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  onClick={() => { setEditingRec(null); setRecForm({ name: '', nameTh: '', position: '', email: '', department: '', location: '', phone: '' }); openRec() }}
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                  {t('config.addRecipient','Add Recipient')}
                </Button>
                <ImportExportButtons resource="recipients" onImported={loadRecipients} />
              </Group>
            </Group>
            <CrudTable
              data={(() => {
                const q = recSearch.toLowerCase()
                return recipients.filter((r: any) => {
                  const matchQ = !q || r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.department?.toLowerCase().includes(q) || r.position?.toLowerCase().includes(q)
                  const matchDept = !recDeptFilter || r.department === recDeptFilter
                  return matchQ && matchDept
                }).map((r: any) => ({ ...r, displayName: localizedNameWithEn(r, i18n.language) }))
              })()}
              columns={[
                { key: 'displayName', label: t('config.colName','Name') },
                { key: 'position', label: t('config.colPosition','Position') },
                { key: 'email', label: t('config.colEmail','Email') },
                { key: 'department', label: t('config.colDepartment','Department') },
                { key: 'location', label: t('config.colLocation','Location') },
                { key: 'phone', label: t('config.colPhone','Phone') },
              ]}
              onEdit={(r) => { setEditingRec(r); setRecForm({ name: r.name, nameTh: r.nameTh ?? '', position: r.position ?? '', email: r.email ?? '', department: r.department ?? '', location: r.location ?? '', phone: r.phone ?? '' }); openRec() }}
              onDelete={(r) => handleDelete('Recipient', () => deleteRecipient(r.id), loadRecipients)}
              onDeactivate={(r) => confirmDeactivate('Recipient', () => deactivateRecipient(r.id), loadRecipients)} canDelete={canDeleteConfig}
            />
          </Tabs.Panel>

          {/* ── BRANDS TAB ── */}
          <Tabs.Panel value="brands">
            <Group justify="space-between" mb="md">
              <Group gap="sm" />
              <Group gap="sm">
                <TextInput
                  placeholder={t("config.searchCodeOrName","Search code or name...")}
                  leftSection={<IconSearch size={14} />}
                  value={brandSearch}
                  onChange={e => setBrandSearch(e.target.value)}
                  size="sm"
                  style={{ width: 200 }}
                  styles={{ input: { borderColor: '#e2e8f0' } }}
                />
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  onClick={() => { setEditingBrand(null); setBrandForm({ code: '', name: '', nameTh: '', description: '' }); openBrand() }}
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                  {t('config.addBrand','Add Brand')}
                </Button>
                <ImportExportButtons resource="brands" onImported={loadBrands} />
              </Group>
            </Group>
            <CrudTable
              data={(() => { const q = brandSearch.toLowerCase(); return q ? brands.filter(b => b.code?.toLowerCase().includes(q) || b.name?.toLowerCase().includes(q)) : brands })()}
              columns={[{ key: 'code', label: t('config.colCode','Code') }, { key: 'name', label: t('config.colBrandName','Brand Name') }, { key: 'nameTh', label: t('config.colNameTh','ชื่อภาษาไทย') }, { key: 'description', label: t('config.colDescription','Description') }]}
              onEdit={(b) => { setEditingBrand(b); setBrandForm({ code: b.code, name: b.name, nameTh: b.nameTh ?? '', description: b.description ?? '' }); openBrand() }}
              onDelete={(b) => handleDelete('Brand', () => deleteBrand(b.id), loadBrands)}
              onDeactivate={(b) => confirmDeactivate('Brand', () => deactivateBrand(b.id), loadBrands)} canDelete={canDeleteConfig}
            />
          </Tabs.Panel>

          {/* ── VENDORS TAB ── */}
          <Tabs.Panel value="vendors">
            <Group justify="space-between" mb="md">
              <Group gap="sm" />
              <Group gap="sm">
                <TextInput
                  placeholder={t("config.searchNameContactEmail","Search name, contact, email...")}
                  leftSection={<IconSearch size={14} />}
                  value={vendorSearch}
                  onChange={e => setVendorSearch(e.target.value)}
                  size="sm"
                  style={{ width: 240 }}
                  styles={{ input: { borderColor: '#e2e8f0' } }}
                />
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  onClick={() => { setEditingVendor(null); setVendorForm({ name: '', nameTh: '', contactName: '', phone: '', email: '', address: '' }); openVendor() }}
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                  {t('config.addVendor','Add Vendor')}
                </Button>
                <ImportExportButtons resource="vendors" onImported={loadVendors} />
              </Group>
            </Group>
            <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #ebebeb', overflow: 'auto' }}>
              {isMobile ? (
                (() => {
                  const q = vendorSearch.toLowerCase()
                  const filtered = q ? vendorsList.filter(v =>
                    v.name?.toLowerCase().includes(q) ||
                    v.contactName?.toLowerCase().includes(q) ||
                    v.email?.toLowerCase().includes(q) ||
                    v.phone?.toLowerCase().includes(q)
                  ) : vendorsList
                  if (filtered.length === 0) return (
                    <Center p="xl"><Text c="dimmed" size="sm">{t("config.noVendors","No vendors found")}</Text></Center>
                  )
                  return (
                    <Stack gap="xs" p="xs">
                      {filtered.map(v => (
                        <Card key={v.id} withBorder radius="lg" p="md" style={{ opacity: v.isActive === false ? 0.55 : 1 }}>
                          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                            <Box style={{ minWidth: 0, flex: 1 }}>
                              <Text fw={700} size="md" style={{ fontFamily: 'Inter, sans-serif', wordBreak: 'break-word' }}>{v.name}</Text>
                              {v.nameTh && <Text size="xs" c="dimmed">{v.nameTh}</Text>}
                            </Box>
                            <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                              <Badge color={v.isActive !== false ? 'lime' : 'gray'} variant="light" size="sm">
                                {v.isActive !== false ? t('config.active','Active') : t('config.inactive','Inactive')}
                              </Badge>
                              <Menu shadow="md" width={200} position="bottom-end">
                                <Menu.Target>
                                  <ActionIcon variant="subtle" color="gray" size="lg"><IconDotsVertical size={18} /></ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <Menu.Item leftSection={<IconEdit size={15} />}
                                    onClick={() => { setEditingVendor(v); setVendorForm({ name: v.name, nameTh: v.nameTh ?? '', contactName: v.contactName ?? '', phone: v.phone ?? '', email: v.email ?? '', address: v.address ?? '' }); openVendor() }}>
                                    {t('config.edit','Edit')}
                                  </Menu.Item>
                                  {canDeleteConfig && v.isActive !== false && (
                                    <Menu.Item leftSection={<IconBan size={15} />} color="orange"
                                      onClick={() => confirmDeactivate('Vendor', () => deactivateVendor(v.id), loadVendors)}>
                                      {t('config.deactivate','Deactivate')}
                                    </Menu.Item>
                                  )}
                                  {canDeleteConfig && (
                                    <Menu.Item leftSection={<IconTrash size={15} />} color="red"
                                      onClick={() => handleDelete('Vendor', () => deleteVendor(v.id), loadVendors)}>
                                      {t('config.deletePerm','Delete permanently')}
                                    </Menu.Item>
                                  )}
                                </Menu.Dropdown>
                              </Menu>
                            </Group>
                          </Group>
                          {(v.contactName || v.phone || v.email) && (
                            <>
                              <Divider my={8} />
                              <Stack gap={4}>
                                {v.contactName && <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 70 }}>{t("config.colContact","Contact")}</Text><Text size="xs">{v.contactName}</Text></Group>}
                                {v.phone && <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 70 }}>{t("config.colPhone","Phone")}</Text><Text size="xs">{v.phone}</Text></Group>}
                                {v.email && <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 70 }}>{t("config.colEmail","Email")}</Text><Text size="xs" style={{ wordBreak: 'break-word' }}>{v.email}</Text></Group>}
                              </Stack>
                            </>
                          )}
                        </Card>
                      ))}
                    </Stack>
                  )
                })()
              ) : (
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8f7f4' }}>
                  <Table.Tr>
                    <Table.Th>{t("config.colVendorName","Vendor Name")}</Table.Th>
                    <Table.Th>{t("config.colNameTh","ชื่อภาษาไทย")}</Table.Th>
                    <Table.Th>{t("config.colContact","Contact")}</Table.Th>
                    <Table.Th>{t("config.colPhone","Phone")}</Table.Th>
                    <Table.Th>{t("config.colEmail","Email")}</Table.Th>
                    <Table.Th>{t('config.statusHeader','Status')}</Table.Th>
                    <Table.Th></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(() => {
                    const q = vendorSearch.toLowerCase()
                    const filtered = q ? vendorsList.filter(v =>
                      v.name?.toLowerCase().includes(q) ||
                      v.contactName?.toLowerCase().includes(q) ||
                      v.email?.toLowerCase().includes(q) ||
                      v.phone?.toLowerCase().includes(q)
                    ) : vendorsList
                    if (filtered.length === 0) return (
                      <Table.Tr>
                        <Table.Td colSpan={6}><Center p="xl"><Text c="dimmed" size="sm">{t("config.noVendors","No vendors found")}</Text></Center></Table.Td>
                      </Table.Tr>
                    )
                    return filtered.map(v => (
                      <Table.Tr key={v.id} style={{ opacity: v.isActive === false ? 0.5 : 1 }}>
                        <Table.Td><Text size="sm" fw={700} style={{ fontFamily: 'Inter, sans-serif' }}>{v.name}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{v.nameTh ?? '-'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{v.contactName ?? '-'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{v.phone ?? '-'}</Text></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{v.email ?? '-'}</Text></Table.Td>
                        <Table.Td>
                          <Badge color={v.isActive !== false ? 'lime' : 'gray'} variant="light" size="sm">
                            {v.isActive !== false ? t('config.active','Active') : t('config.inactive','Inactive')}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Tooltip label={t("config.edit","Edit")}>
                              <ActionIcon variant="subtle" color="gray"
                                onClick={() => { setEditingVendor(v); setVendorForm({ name: v.name, nameTh: v.nameTh ?? '', contactName: v.contactName ?? '', phone: v.phone ?? '', email: v.email ?? '', address: v.address ?? '' }); openVendor() }}>
                                <IconEdit size={16} />
                              </ActionIcon>
                            </Tooltip>
                            {canDeleteConfig && v.isActive !== false && (
                              <Tooltip label={t("config.deactivate","Deactivate")}>
                                <ActionIcon variant="subtle" color="orange"
                                  onClick={() => confirmDeactivate('Vendor', () => deactivateVendor(v.id), loadVendors)}>
                                  <IconBan size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            {canDeleteConfig && (
                              <Tooltip label={t("config.deletePerm","Delete permanently")}>
                                <ActionIcon variant="subtle" color="red"
                                  onClick={() => handleDelete('Vendor', () => deleteVendor(v.id), loadVendors)}>
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  })()}
                </Table.Tbody>
              </Table>
              )}
            </Box>
          </Tabs.Panel>

          {/* ── PRODUCTS TAB ── */}
          <Tabs.Panel value="models">
            <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
              <Group gap="sm" wrap="wrap">
                <Select
                  placeholder={t("config.filterByBrand","Filter by brand")}
                  size="xs"
                  clearable
                  data={brands.filter(b => b.isActive !== false).map(b => ({ value: b.code, label: localizedNameWithEn(b, i18n.language) }))}
                  value={modelBrandFilter}
                  onChange={(v) => setModelBrandFilter(v)}
                  style={{ width: 160 }}
                />
                <Select
                  placeholder={t("config.filterByCategory","Filter by category")}
                  size="xs"
                  clearable
                  data={cats.filter(c => c.isActive !== false).map(c => ({ value: c.code, label: localizedName(c, i18n.language) }))}
                  value={modelCategoryFilter}
                  onChange={(v) => setModelCategoryFilter(v)}
                  style={{ width: 160 }}
                />
              </Group>
              <Group gap="sm" wrap="wrap">
                <TextInput
                  placeholder={t("config.searchCodeNameBrand","Search code, name, brand...")}
                  leftSection={<IconSearch size={14} />}
                  value={modelSearch}
                  onChange={(e) => setModelSearch(e.target.value)}
                  style={{ width: 220 }}
                  size="sm"
                />
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  onClick={() => { setEditingModel(null); setModelForm({ code: '', name: '', nameTh: '', brandCode: modelBrandFilter ?? '', categoryCode: modelCategoryFilter ?? '', subCategoryCode: '', description: '', departmentCode: '' }); setModelImageFile(null); setModelImagePreview(null); openModel() }}
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
                  {t('config.addProduct','Add Product')}
                </Button>
                <ImportExportButtons resource="models" onImported={() => loadModels(modelBrandFilter ?? undefined)} />
              </Group>
            </Group>

            {/* Products Table */}
            <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #ebebeb', overflow: 'auto' }}>
              {isMobile ? (
                (() => {
                  const q = modelSearch.toLowerCase()
                  const filtered = models.filter(m => {
                    const matchSearch = !q || (
                      m.code?.toLowerCase().includes(q) ||
                      m.name?.toLowerCase().includes(q) ||
                      m.brandCode?.toLowerCase().includes(q) ||
                      m.categoryCode?.toLowerCase().includes(q)
                    )
                    const matchBrand = !modelBrandFilter || m.brandCode === modelBrandFilter
                    const matchCat = !modelCategoryFilter || m.categoryCode === modelCategoryFilter
                    return matchSearch && matchBrand && matchCat
                  })
                  if (filtered.length === 0) return (
                    <Center p="xl"><Text c="dimmed" size="sm">{t("config.noProducts","No products found")}</Text></Center>
                  )
                  return (
                    <Stack gap="xs" p="xs">
                      {filtered.map(m => {
                        const cat = m.categoryCode ? cats.find(c => c.code === m.categoryCode) : null
                        const sub = m.subCategoryCode ? subCats.find(s => s.code === m.subCategoryCode) : null
                        return (
                          <Card key={m.id} withBorder radius="lg" p="md" style={{ opacity: m.isActive === false ? 0.55 : 1 }}>
                            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                              {m.imageUrl ? (
                                <Image src={m.imageUrl} w={48} h={48} radius="sm" fit="cover" style={{ border: '1px solid #ebebeb', flexShrink: 0 }} />
                              ) : (
                                <Box w={48} h={48} style={{ background: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <IconPhoto size={20} color="#bbb" />
                                </Box>
                              )}
                              <Box style={{ minWidth: 0, flex: 1 }}>
                                <Badge variant="light" color="dark" size="sm" mb={4} style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>{m.code}</Badge>
                                <Text fw={700} size="md" style={{ wordBreak: 'break-word' }}>{m.name}</Text>
                              </Box>
                              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                                <Badge color={m.isActive !== false ? 'lime' : 'gray'} variant="light" size="sm">
                                  {m.isActive !== false ? t('config.active','Active') : t('config.inactive','Inactive')}
                                </Badge>
                                <Menu shadow="md" width={200} position="bottom-end">
                                  <Menu.Target>
                                    <ActionIcon variant="subtle" color="gray" size="lg"><IconDotsVertical size={18} /></ActionIcon>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    <Menu.Item leftSection={<IconEdit size={15} />}
                                      onClick={() => { setEditingModel(m); setModelForm({ code: m.code, name: m.name, nameTh: m.nameTh ?? '', brandCode: m.brandCode ?? '', categoryCode: m.categoryCode ?? '', subCategoryCode: m.subCategoryCode ?? '', description: m.description ?? '', departmentCode: m.departmentCode ?? '' }); setModelImageFile(null); setModelImagePreview(m.imageUrl ?? null); openModel() }}>
                                      {t('config.edit','Edit')}
                                    </Menu.Item>
                                    {canDeleteConfig && m.isActive !== false && (
                                      <Menu.Item leftSection={<IconBan size={15} />} color="orange"
                                        onClick={() => confirmDeactivate('Product', () => deactivateModel(m.id), loadModels)}>
                                        {t('config.deactivate','Deactivate')}
                                      </Menu.Item>
                                    )}
                                    {canDeleteConfig && (
                                      <Menu.Item leftSection={<IconTrash size={15} />} color="red"
                                        onClick={() => handleDelete('Product', () => deleteModel(m.id), loadModels)}>
                                        {t('config.deletePerm','Delete permanently')}
                                      </Menu.Item>
                                    )}
                                  </Menu.Dropdown>
                                </Menu>
                              </Group>
                            </Group>
                            {(m.brandCode || cat || sub || m.departmentCode) && (
                              <>
                                <Divider my={8} />
                                <Group gap={6}>
                                  {m.brandCode && <Badge size="xs" variant="light" color="blue">{m.brandCode}</Badge>}
                                  {cat && <Badge size="xs" variant="light" color="teal">{cat.name}</Badge>}
                                  {sub && <Badge size="xs" variant="light" color="violet">{sub.name}</Badge>}
                                  {m.departmentCode && <Badge size="xs" variant="light" color="orange">🏢 {m.departmentCode}</Badge>}
                                </Group>
                              </>
                            )}
                          </Card>
                        )
                      })}
                    </Stack>
                  )
                })()
              ) : (
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8f7f4' }}>
                  <Table.Tr>
                    <Table.Th style={{ width: 64 }}>{t("config.colImage","Image")}</Table.Th>
                    <Table.Th>{t("config.colCode","Code")}</Table.Th>
                    <Table.Th>{t("config.colProductName","Product Name")}</Table.Th>
                    <Table.Th>{t("config.colBrand","Brand")}</Table.Th>
                    <Table.Th>{t("config.colCategory","Category")}</Table.Th>
                    <Table.Th>{t("config.colSubCategory","Sub-Category")}</Table.Th>
                    <Table.Th>{t('config.statusHeader','Status')}</Table.Th>
                    <Table.Th></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(() => {
                    const q = modelSearch.toLowerCase()
                    const filtered = models.filter(m => {
                      const matchSearch = !q || (
                        m.code?.toLowerCase().includes(q) ||
                        m.name?.toLowerCase().includes(q) ||
                        m.brandCode?.toLowerCase().includes(q) ||
                        m.categoryCode?.toLowerCase().includes(q)
                      )
                      const matchBrand = !modelBrandFilter || m.brandCode === modelBrandFilter
                      const matchCat = !modelCategoryFilter || m.categoryCode === modelCategoryFilter
                      return matchSearch && matchBrand && matchCat
                    })

                    if (filtered.length === 0) return (
                      <Table.Tr>
                        <Table.Td colSpan={9}>
                          <Center p="xl"><Text c="dimmed" size="sm">{t("config.noProducts","No products found")}</Text></Center>
                        </Table.Td>
                      </Table.Tr>
                    )

                    return filtered.map(m => (
                      <Table.Tr key={m.id} style={{ opacity: m.isActive === false ? 0.5 : 1 }}>
                        <Table.Td>
                          {m.imageUrl ? (
                            <Image src={m.imageUrl} w={44} h={44} radius="sm" fit="cover"
                              style={{ border: '1px solid #ebebeb' }} />
                          ) : (
                            <Box w={44} h={44} style={{ background: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <IconPhoto size={18} color="#bbb" />
                            </Box>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={700} style={{ fontFamily: 'Inter, sans-serif' }}>{m.code}</Text>
                        </Table.Td>
                        <Table.Td><Text size="sm">{m.name}</Text></Table.Td>
                        <Table.Td>
                          {m.brandCode ? (
                            <Badge size="xs" variant="light" color="blue">{m.brandCode}</Badge>
                          ) : <Text size="sm" c="dimmed">-</Text>}
                        </Table.Td>
                        <Table.Td>
                          {m.categoryCode ? (() => {
                            const cat = cats.find(c => c.code === m.categoryCode)
                            return <Badge size="xs" variant="light" color="teal">{cat?.name ?? m.categoryCode}</Badge>
                          })() : <Text size="sm" c="dimmed">-</Text>}
                        </Table.Td>
                        <Table.Td>
                          {m.subCategoryCode ? (() => {
                            const sub = subCats.find(s => s.code === m.subCategoryCode)
                            return <Badge size="xs" variant="light" color="violet">{sub?.name ?? m.subCategoryCode}</Badge>
                          })() : <Text size="xs" c="dimmed">-</Text>}
                        </Table.Td>
                        <Table.Td>
                          <Badge color={m.isActive !== false ? 'lime' : 'gray'} variant="light" size="sm">
                            {m.isActive !== false ? t('config.active','Active') : t('config.inactive','Inactive')}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Tooltip label={t("config.edit","Edit")}>
                              <ActionIcon variant="subtle" color="gray"
                                onClick={() => { setEditingModel(m); setModelForm({ code: m.code, name: m.name, nameTh: m.nameTh ?? '', brandCode: m.brandCode ?? '', categoryCode: m.categoryCode ?? '', subCategoryCode: m.subCategoryCode ?? '', description: m.description ?? '', departmentCode: m.departmentCode ?? '' }); setModelImageFile(null); setModelImagePreview(m.imageUrl ?? null); openModel() }}>
                                <IconEdit size={16} />
                              </ActionIcon>
                            </Tooltip>
                            {canDeleteConfig && m.isActive !== false && (
                              <Tooltip label={t("config.deactivate","Deactivate")}>
                                <ActionIcon variant="subtle" color="orange"
                                  onClick={() => confirmDeactivate('Product', () => deactivateModel(m.id), loadModels)}>
                                  <IconBan size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            {canDeleteConfig && (
                              <Tooltip label={t("config.deletePerm","Delete permanently")}>
                                <ActionIcon variant="subtle" color="red"
                                  onClick={() => handleDelete('Product', () => deleteModel(m.id), loadModels)}>
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  })()}
                </Table.Tbody>
              </Table>
              )}
            </Box>
          </Tabs.Panel>

          {/* ══ COMSET TABS ══ */}
          <Tabs.Panel value="sw-library" pt="md"><SwLibraryTab /></Tabs.Panel>
          <Tabs.Panel value="chk-items" pt="md"><ChecklistItemsTab /></Tabs.Panel>
          <Tabs.Panel value="chk-templates" pt="md"><CheckTemplatesTab /></Tabs.Panel>
          <Tabs.Panel value="comsets" pt="md"><ComsetTab /></Tabs.Panel>

          {/* ══ NOTIFICATIONS (LINE Reminder) ══ */}
          <Tabs.Panel value="notifications" pt="md">
            <NotificationsPanel t={t} />
          </Tabs.Panel>

          {/* ══ STOCK LOCATIONS ══ */}
          <Tabs.Panel value="stock-locations" pt="md">
            <StockLocationsTab />
          </Tabs.Panel>

          {/* ══ BORROW POLICY ══ */}
          <Tabs.Panel value="borrow-policy" pt="md">
            <BorrowPolicyTab />
          </Tabs.Panel>

                    </Tabs>
          </Box>
        </Box>
      )}

      {/* ══ DEPARTMENT MODAL ══ */}
      <FormModal opened={deptOpen} onClose={closeDept} onSave={saveDept}
        editing={editingDept} saving={saving}
        title={editingDept ? t('config.editDept','Edit Department') : t('config.addDept','Add Department')}>
        <TextInput label={t("config.fieldCode","Code")+' *'} placeholder={t("config.codeExamplePlaceholder","IT")} value={deptForm.code}
          onChange={(e) => setDeptForm((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 10) }))}
          description="English uppercase only (A-Z, 0-9, -, _)"
          disabled={!!editingDept} />
        <TextInput label={t("config.fieldNameEN","Name (EN) *")} placeholder="Information Technology" value={deptForm.name}
          onChange={(e) => setDeptForm((p) => ({ ...p, name: filterEnglishOnly(e.target.value) }))} />
        <TextInput label="ชื่อ (TH)" placeholder="เทคโนโลยีสารสนเทศ" value={deptForm.nameTh}
          onChange={(e) => setDeptForm((p) => ({ ...p, nameTh: e.target.value }))} />
        <TextInput label={t('common.description')} value={deptForm.description}
          onChange={(e) => setDeptForm((p) => ({ ...p, description: e.target.value }))} />
      </FormModal>

      {/* ══ CATEGORY MODAL ══ */}
      <FormModal opened={catOpen} onClose={closeCat} onSave={saveCat}
        editing={editingCat} saving={saving}
        title={editingCat ? t('config.editCategory','Edit Category') : t('config.addCategory','Add Category')}>
        <TextInput label={t("config.fieldCode","Code")+' *'} placeholder="LAPTOP" value={catForm.code}
          onChange={(e) => setCatForm((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 10) }))}
          description="English uppercase only"
          disabled={!!editingCat} />
        <TextInput label={t("config.fieldNameEN","Name (EN) *")} placeholder="Laptop" value={catForm.name}
          onChange={(e) => setCatForm((p) => ({ ...p, name: filterEnglishOnly(e.target.value) }))} />
        <TextInput label="ชื่อ (TH)" placeholder="แล็ปท็อป" value={catForm.nameTh}
          onChange={(e) => setCatForm((p) => ({ ...p, nameTh: e.target.value }))} />
        <TextInput label={t('common.description')} value={catForm.description}
          onChange={(e) => setCatForm((p) => ({ ...p, description: e.target.value }))} />
        {/* Department scope */}
        <Box>
          <Text size="sm" fw={500} mb={4}>{t("config.departmentScopeLabel","Department")} <Text component="span" size="xs" c="dimmed">{t("config.leaveBlankGlobal","(leave blank = global)")}</Text></Text>
          <Select
            placeholder={t("config.globalAvailableAllDepts","Global — available to all departments")}
            data={depts.filter((d) => d.isActive !== false).map((d) => ({ value: d.code, label: `${d.name}  (${d.code})` }))}
            value={catForm.departmentCode || null}
            onChange={(v) => setCatForm((p) => ({ ...p, departmentCode: v ?? '' }))}
            clearable
          />
        </Box>
      </FormModal>

      {/* ══ SUB-CATEGORY MODAL ══ */}
      <FormModal opened={subCatOpen} onClose={closeSubCat} onSave={saveSubCat}
        editing={editingSubCat} saving={saving}
        title={editingSubCat ? t('config.editSubCat','Edit Sub-Category') : t('config.addSubCat','Add Sub-Category')}>
        <TextInput label={t("config.fieldCode","Code")+' *'} placeholder="MACBOOK" value={subCatForm.code}
          onChange={(e) => setSubCatForm((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 10) }))}
          description="English uppercase only"
          disabled={!!editingSubCat} />
        <TextInput label={t("config.fieldNameEN","Name (EN) *")} placeholder="MacBook" value={subCatForm.name}
          onChange={(e) => setSubCatForm((p) => ({ ...p, name: filterEnglishOnly(e.target.value) }))} />
        <TextInput label="ชื่อ (TH)" placeholder="แมคบุ๊ก" value={subCatForm.nameTh}
          onChange={(e) => setSubCatForm((p) => ({ ...p, nameTh: e.target.value }))} />
        {/* Parent Category */}
        <Box>
          <Text size="sm" fw={500} mb={4}>{t("config.fieldParentCategory","Parent Category")}</Text>
          <Select
            placeholder={t('config.selectCategoryPlaceholder','Select category...')}
            data={cats.filter((c) => c.isActive !== false).map((c) => ({
              value: c.code,
              label: `${c.name}  (${c.code})${c.departmentCode ? ` — ${c.departmentCode}` : ' — global'}`,
            }))}
            value={subCatForm.category || null}
            onChange={(v) => setSubCatForm((p) => ({ ...p, category: v ?? '' }))}
            clearable searchable
          />
        </Box>
        {/* Department scope */}
        <Box>
          <Text size="sm" fw={500} mb={4}>{t("config.departmentScopeLabel","Department")} <Text component="span" size="xs" c="dimmed">{t("config.leaveBlankGlobal","(leave blank = global)")}</Text></Text>
          <Select
            placeholder={t("config.globalAvailableAllDepts","Global — available to all departments")}
            data={depts.filter((d) => d.isActive !== false).map((d) => ({ value: d.code, label: `${d.name}  (${d.code})` }))}
            value={subCatForm.departmentCode || null}
            onChange={(v) => setSubCatForm((p) => ({ ...p, departmentCode: v ?? '' }))}
            clearable
          />
        </Box>
      </FormModal>
      {/* Location Form Modal */}
      <FormModal opened={locationOpen} onClose={closeLoc} onSave={saveLocation}
        editing={editingLocation} saving={saving}
        title={editingLocation ? t('config.editLocation','Edit Location') : t('config.addLocation','Add Location')}>
        <TextInput label={t("config.fieldCode","Code")+' *'} placeholder="OFFICE-A-F1" value={locationForm.code}
          onChange={(e) => setLocationForm((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 10) }))}
          description="English uppercase only"
          disabled={!!editingLocation} />
        <TextInput label={t("config.fieldNameEN","Name (EN) *")} placeholder="Office A - Floor 1" value={locationForm.name}
          onChange={(e) => setLocationForm((p) => ({ ...p, name: filterEnglishOnly(e.target.value) }))} />
        <TextInput label="ชื่อ (TH)" placeholder="ออฟฟิศ A ชั้น 1" value={locationForm.nameTh}
          onChange={(e) => setLocationForm((p) => ({ ...p, nameTh: e.target.value }))} />
        <TextInput label={t('common.description')} placeholder={t("config.roomHintPlaceholder","Server room, meeting room, etc.")} value={locationForm.description}
          onChange={(e) => setLocationForm((p) => ({ ...p, description: e.target.value }))} />
      </FormModal>
      {/* ── Vendor Form Modal ── */}
      <FormModal opened={vendorOpen} onClose={closeVendor} onSave={saveVendor}
        editing={editingVendor} saving={saving}
        title={editingVendor ? t('config.editVendor','Edit Vendor') : t('config.addVendor','Add Vendor')}>
        <TextInput label={t("config.fieldVendorName","Vendor Name (EN) *")} placeholder="e.g. Express Data, Apple Thailand"
          value={vendorForm.name} onChange={(e) => setVendorForm(p => ({ ...p, name: filterEnglishOnly(e.target.value) }))} />
        <TextInput label={t('common.nameTh','ชื่อภาษาไทย')} placeholder="เช่น บริษัท เอ็กซ์เพรส ดาต้า จำกัด"
          value={vendorForm.nameTh} onChange={(e) => setVendorForm(p => ({ ...p, nameTh: e.target.value }))} />
        <TextInput label={t('common.contactPerson')} placeholder="e.g. John Smith"
          value={vendorForm.contactName} onChange={(e) => setVendorForm(p => ({ ...p, contactName: e.target.value }))} />
        <TextInput label={t('common.phone')} placeholder="e.g. 02-xxx-xxxx"
          value={vendorForm.phone} onChange={(e) => setVendorForm(p => ({ ...p, phone: e.target.value }))} />
        <TextInput label={t("config.emailLabel","Email")} placeholder="e.g. sales@vendor.com"
          value={vendorForm.email} onChange={(e) => setVendorForm(p => ({ ...p, email: e.target.value }))} />
        <TextInput label={t('common.address')}
          value={vendorForm.address} onChange={(e) => setVendorForm(p => ({ ...p, address: e.target.value }))} />
      </FormModal>

      {/* ── Brand Form Modal ── */}
      <FormModal opened={brandOpen} onClose={closeBrand} onSave={saveBrand}
        editing={editingBrand} saving={saving}
        title={editingBrand ? t('config.editBrand','Edit Brand') : t('config.addBrand','Add Brand')}>
        <TextInput label={t("config.fieldCode","Code")+' *'} placeholder="e.g. APPLE, SAMSUNG, DELL"
          value={brandForm.code} onChange={(e) => setBrandForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 10) }))}
          disabled={!!editingBrand} />
        <TextInput label="Brand Name (EN) *" placeholder="e.g. Apple, Samsung, Dell"
          value={brandForm.name} onChange={(e) => setBrandForm(p => ({ ...p, name: filterEnglishOnly(e.target.value) }))} />
        <TextInput label="ชื่อแบรนด์ (TH)" placeholder="เช่น แอปเปิ้ล, ซัมซุง, เดลล์"
          value={brandForm.nameTh}
          onChange={(e) => setBrandForm(p => ({ ...p, nameTh: e.target.value }))} />
        <TextInput label={t('common.description')}
          value={brandForm.description} onChange={(e) => setBrandForm(p => ({ ...p, description: e.target.value }))} />
      </FormModal>

      {/* ── Product Form Modal ── */}
      <FormModal opened={modelOpen} onClose={() => { closeModel(); setModelImageFile(null); setModelImagePreview(null) }} onSave={saveModel}
        editing={editingModel} saving={saving || modelImageUploading}
        title={editingModel ? t('config.editProduct','Edit Product') : t('config.addProduct','Add Product')}>
        <TextInput label={t("config.fieldCode","Code")+' *'} placeholder="e.g. MACBOOK-PRO-14, S24-ULTRA"
          value={modelForm.code} onChange={(e) => setModelForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 10) }))}
          disabled={!!editingModel} />
        <TextInput label="Product Name (EN) *" placeholder="e.g. MacBook Pro 14, Galaxy S24 Ultra"
          value={modelForm.name} onChange={(e) => setModelForm(p => ({ ...p, name: filterEnglishOnly(e.target.value) }))} />
        <TextInput label="ชื่อผลิตภัณฑ์ (TH)" placeholder="เช่น แมคบุ๊กโปร 14, กาแล็คซี่ เอส24 อัลตร้า"
          value={modelForm.nameTh}
          onChange={(e) => setModelForm(p => ({ ...p, nameTh: e.target.value }))} />
        <Select
          label={t("config.fieldBrand","Brand")}
          placeholder={t('config.selectBrandPlaceholder','Select brand')}
          data={brands.filter(b => b.isActive !== false).map(b => ({ value: b.code, label: localizedNameWithEn(b, i18n.language) }))}
          value={modelForm.brandCode || null}
          onChange={(v) => setModelForm(p => ({ ...p, brandCode: v ?? '' }))}
          searchable clearable
        />
        <Select
          label={t("config.fieldCategory","Category")}
          placeholder={t('config.selectCategoryPlaceholder','Select category...')}
          data={cats.filter(c => c.isActive !== false).map(c => ({ value: c.code, label: `${localizedName(c, i18n.language)} (${c.code})` }))}
          value={modelForm.categoryCode || null}
          onChange={(v) => setModelForm(p => ({ ...p, categoryCode: v ?? '', subCategoryCode: '' }))}
          searchable clearable
        />
        {/* Sub-Category — filtered by selected category */}
        {(() => {
          const subOptions = subCats
            .filter(s => s.isActive !== false && (!modelForm.categoryCode || s.category === modelForm.categoryCode))
          return subOptions.length > 0 ? (
            <Select
              label={t("config.fieldSubCategory","Sub-Category")}
              placeholder={t("config.selectSubCategoryOptional","Select sub-category (optional)")}
              data={subOptions.map(s => ({ value: s.code, label: `${localizedName(s, i18n.language)} (${s.code})` }))}
              value={modelForm.subCategoryCode || null}
              onChange={(v) => setModelForm(p => ({ ...p, subCategoryCode: v ?? '' }))}
              searchable clearable
            />
          ) : modelForm.categoryCode ? (
            <Text size="xs" c="dimmed" mt={-4}>— ไม่มี Sub-Category สำหรับ category นี้</Text>
          ) : null
        })()}
        <TextInput label={t('common.description')}
          value={modelForm.description} onChange={(e) => setModelForm(p => ({ ...p, description: e.target.value }))} />

        {/* Department scope — เดิม modelForm ไม่มี field นี้เลย ทั้งที่ backend (getModels/createModel
        ใน configuration.service.ts) รองรับ departmentCode อยู่แล้วเหมือนกับ Category/SubCategory
        เป๊ะ (ว่างไว้ = global เห็นทุกแผนก, ระบุ = เฉพาะแผนกนั้น + ยังอนุญาตให้โค้ดซ้ำกันได้ระหว่าง
        ต่างแผนกด้วย) — เพิ่ม UI ให้ตรงกับที่ Category ใช้อยู่แล้ว
        ⚠️ สำคัญ: ที่ controller createModel() จะ "บังคับ" departmentCode เป็นแผนกของ user เอง
        เสมอถ้า user ไม่ใช่ ADMIN หรือ ASSET_MANAGER ที่ไม่ผูกแผนก (ดู getUserDept() ใน
        configuration.controller.ts) — แต่ updateModel() ไม่ได้บังคับแบบนี้เลย (ส่ง dto ตรงๆ) จึงต้อง
        แยกเงื่อนไข: ตอนสร้างใหม่ (ไม่มี editingModel) ถ้า user ถูกจำกัดแผนกอยู่แล้ว เลือกอะไรก็ถูก
        เขียนทับเงียบๆ อยู่ดี เลยล็อกไว้ไม่ให้สับสน — แต่ตอนแก้ไขรายการที่มีอยู่แล้ว ทุก role
        แก้ค่านี้ได้จริงตามที่ backend รองรับ */}
        {(roles.includes('ADMIN') || (roles.includes('ASSET_MANAGER') && userDepts.length === 0) || !!editingModel) ? (
          <Box>
            <Text size="sm" fw={500} mb={4}>{t("config.departmentScopeLabel","Department")} <Text component="span" size="xs" c="dimmed">{t("config.leaveBlankGlobal","(leave blank = global)")}</Text></Text>
            <Select
              placeholder={t("config.globalAvailableAllDepts","Global — available to all departments")}
              data={depts.filter((d) => d.isActive !== false).map((d) => ({ value: d.code, label: `${d.name}  (${d.code})` }))}
              value={modelForm.departmentCode || null}
              onChange={(v) => setModelForm(p => ({ ...p, departmentCode: v ?? '' }))}
              clearable searchable
            />
          </Box>
        ) : (
          <Box>
            <Text size="sm" fw={500} mb={4}>{t("config.departmentScopeLabel","Department")}</Text>
            <Text size="sm" c="dimmed">{t("config.departmentAutoAssigned","Automatically set to your department")} — {userDepts[0] ?? '-'}</Text>
          </Box>
        )}

        {/* ── Model Image (400×400) ── */}
        <Box>
          <Text size="sm" fw={600} mb={6}>{t("config.modelImageLabel","Model Image")} <Text span size="xs" c="dimmed">{t("config.autoCroppedHint","(400×400 px — will be auto-cropped)")}</Text></Text>
          <Group align="flex-start" gap="md">
            <Box w={100} h={100} style={{ background: '#f8f7f4', borderRadius: 10, border: '1.5px dashed #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              {modelImagePreview ? (
                <Image src={modelImagePreview} w={100} h={100} fit="cover" />
              ) : (
                <IconPhoto size={28} color="#ccc" />
              )}
            </Box>
            <Stack gap={6} style={{ flex: 1 }}>
              <FileButton
                onChange={(file) => {
                  if (!file) return
                  setModelImageFile(file)
                  const url = URL.createObjectURL(file)
                  setModelImagePreview(url)
                }}
                accept="image/png,image/jpeg,image/webp"
              >
                {(props) => (
                  <Button {...props} variant="outline" size="xs" leftSection={<IconUpload size={14} />} color="gray">
                    {modelImagePreview ? 'Change Image' : 'Choose Image'}
                  </Button>
                )}
              </FileButton>
              <Text size="xs" c="dimmed">JPG, PNG, WEBP — max 5 MB</Text>
              {modelImagePreview && (
                <ActionIcon
                  size="xs" color="red" variant="subtle"
                  onClick={async () => {
                    if (editingModel?.id && !modelImageFile) {
                      await deleteModelImage(editingModel.id)
                      notifications.show({ color: 'lime', message: 'Image removed' })
                      loadModels()
                    }
                    setModelImageFile(null)
                    setModelImagePreview(null)
                  }}
                >
                  <IconX size={12} /> <Text size="xs" ml={2}>{t("config.remove","Remove")}</Text>
                </ActionIcon>
              )}
            </Stack>
          </Group>
        </Box>
      </FormModal>

      {/* ── Position Form Modal ── */}
      <FormModal opened={posOpen} onClose={closePos} onSave={savePosition}
        editing={editingPos} saving={saving}
        title={editingPos ? t('config.editPosition','Edit Position') : t('config.addPosition','Add Position')}>
        <TextInput label="Position Name (EN) *" placeholder="e.g. IT Staff, Manager, Supervisor"
          value={posForm.name} onChange={(e) => setPosForm(p => ({ ...p, name: e.target.value }))}
          disabled={!!editingPos} />
        <TextInput label="ชื่อตำแหน่ง (TH)" placeholder="เช่น เจ้าหน้าที่ IT, ผู้จัดการ"
          value={posForm.nameTh}
          onChange={(e) => setPosForm(p => ({ ...p, nameTh: e.target.value }))} />
        <TextInput label={t('common.description')} placeholder={t('config.optionalDescriptionPlaceholder','Optional description')}
          value={posForm.description} onChange={(e) => setPosForm(p => ({ ...p, description: e.target.value }))} />
      </FormModal>

      {/* ── Recipient Form Modal ── */}
      <FormModal opened={recOpen} onClose={closeRec} onSave={saveRecipient}
        editing={editingRec} saving={saving}
        title={editingRec ? t('config.editRecipient','Edit Recipient') : t('config.addRecipient','Add Recipient')}>
        <TextInput label="Full Name (EN) *" placeholder="e.g. John Smith"
          value={recForm.name} onChange={(e) => setRecForm(p => ({ ...p, name: e.target.value }))} />
        <TextInput label="ชื่อ (TH)" placeholder="เช่น สมชาย ใจดี"
          value={recForm.nameTh}
          onChange={(e) => setRecForm(p => ({ ...p, nameTh: e.target.value }))} />
        <Select
          label={t("config.fieldPosition","Position")}
          placeholder={t('config.selectPositionPlaceholder','Select position')}
          data={positions.filter(p => p.isActive !== false).map(p => ({ value: p.name, label: localizedNameWithEn(p, i18n.language) }))}
          value={recForm.position || null}
          onChange={(v) => setRecForm(p => ({ ...p, position: v ?? '' }))}
          searchable clearable
        />
        <TextInput label={t("config.emailLabel","Email")} placeholder="email@company.com" type="email"
          value={recForm.email} onChange={(e) => setRecForm(p => ({ ...p, email: e.target.value }))} />
        <Select
          label={t("config.fieldDepartment","Department")}
          placeholder={t('config.selectDepartmentPlaceholder','Select department...')}
          data={depts.filter((d: any) => d.isActive !== false).map((d: any) => ({ value: d.name ?? d.code, label: d.name ?? d.code }))}
          value={recForm.department || null}
          onChange={(v) => setRecForm(p => ({ ...p, department: v ?? '' }))}
          searchable clearable
        />
        <Select
          label={t("config.fieldLocation","Location")}
          placeholder={t('config.selectLocationPlaceholder','Select location...')}
          data={locationsList.filter((l: any) => l.isActive !== false).map((l: any) => ({ value: l.name ?? l.code, label: localizedName(l, i18n.language) || l.code }))}
          value={recForm.location || null}
          onChange={(v) => setRecForm(p => ({ ...p, location: v ?? '' }))}
          searchable clearable
        />
        <TextInput label={t('common.phone')} placeholder="e.g. 081-234-5678"
          value={recForm.phone} onChange={(e) => setRecForm(p => ({ ...p, phone: e.target.value }))} />
      </FormModal>

      {/* ── Location Import Modal ── */}
      <Modal opened={locImportOpen} onClose={() => { closeLocImport(); setLocImportFile(null); setLocImportResult(null) }}
        title={<Group gap="xs"><IconFileSpreadsheet size={20} /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('config.importLocations','Import Locations')}</Text></Group>}
        size="md" radius="lg">
        <Stack gap="md">
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={600} mb={4}>{t("config.requiredCodeName","Required: code, name")}</Text>
            <Text size="xs" c="dimmed">{t("config.optionalDeptDesc","Optional: departmentCode (leave blank = global), description")}</Text>
          </Alert>
          <Button variant="subtle" size="xs" leftSection={<IconUpload size={14} />} onClick={downloadLocationTemplate}>
            {t("config.downloadCsvTemplate","Download CSV Template")}
          </Button>
          <Divider />
          <FileButton onChange={(f) => { setLocImportFile(f); setLocImportResult(null) }} accept=".csv,.xlsx,.xls">
            {(props) => (
              <Button {...props} variant="outline" leftSection={<IconFileSpreadsheet size={16} />} color="gray">
                {locImportFile ? locImportFile.name : t("config.chooseFileCsvXlsx","Choose File (.csv / .xlsx)")}
              </Button>
            )}
          </FileButton>
          {locImportResult && (
            <Box>
              <Group gap="lg" mb="xs">
                <Box ta="center"><Text size="xl" fw={600} c="lime">{locImportResult.imported}</Text><Text size="xs" c="dimmed">{t("config.imported","Imported")}</Text></Box>
                <Box ta="center"><Text size="xl" fw={600} c="orange">{locImportResult.skipped}</Text><Text size="xs" c="dimmed">{t("config.skipped","Skipped")}</Text></Box>
              </Group>
              {locImportResult.errors.length > 0 && (
                <ScrollArea h={120}><List size="xs" spacing={2}>
                  {locImportResult.errors.map((e, i) => <List.Item key={i} icon={<IconAlertCircle size={12} color="orange" />}>{e}</List.Item>)}
                </List></ScrollArea>
              )}
              {locImportResult.imported > 0 && (
                <Alert color="teal" variant="light" icon={<IconCheck size={16} />} mt="xs">{t("config.locationsImportedCount","{{count}} location(s) imported.", { count: locImportResult.imported })}</Alert>
              )}
            </Box>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { closeLocImport(); setLocImportFile(null); setLocImportResult(null) }}>{t("config.close","Close")}</Button>
            <Button leftSection={<IconUpload size={16} />} disabled={!locImportFile || !!locImportResult}
              loading={locImporting} onClick={handleLocImport}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t("config.import","Import")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Brand Import Modal (ADMIN only) ── */}
      <Modal opened={brandImportOpen} onClose={() => { closeBrandImport(); setBrandImportFile(null); setBrandImportResult(null) }}
        title={<Group gap="xs"><IconFileSpreadsheet size={20} /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('config.importBrands','Import Brands')}</Text></Group>}
        size="md" radius="lg">
        <Stack gap="md">
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={600} mb={4}>{t("config.requiredCodeName","Required: code, name")}</Text>
            <Text size="xs" c="dimmed">Optional: nameTh, description, departmentCode (leave blank = global)</Text>
          </Alert>
          <Button variant="subtle" size="xs" leftSection={<IconUpload size={14} />} onClick={downloadBrandTemplate}>
            {t("config.downloadCsvTemplate","Download CSV Template")}
          </Button>
          <Divider />
          <FileButton onChange={(f) => { setBrandImportFile(f); setBrandImportResult(null) }} accept=".csv,.xlsx,.xls">
            {(props) => (
              <Button {...props} variant="outline" leftSection={<IconFileSpreadsheet size={16} />} color="gray">
                {brandImportFile ? brandImportFile.name : t("config.chooseFileCsvXlsx","Choose File (.csv / .xlsx)")}
              </Button>
            )}
          </FileButton>
          {brandImportResult && (
            <Box>
              <Group gap="lg" mb="xs">
                <Box ta="center"><Text size="xl" fw={600} c="lime">{brandImportResult.imported}</Text><Text size="xs" c="dimmed">{t("config.imported","Imported")}</Text></Box>
                <Box ta="center"><Text size="xl" fw={600} c="orange">{brandImportResult.skipped}</Text><Text size="xs" c="dimmed">{t("config.skipped","Skipped")}</Text></Box>
              </Group>
              {brandImportResult.errors.length > 0 && (
                <ScrollArea h={120}><List size="xs" spacing={2}>
                  {brandImportResult.errors.map((e, i) => <List.Item key={i} icon={<IconAlertCircle size={12} color="orange" />}>{e}</List.Item>)}
                </List></ScrollArea>
              )}
              {brandImportResult.imported > 0 && (
                <Alert color="teal" variant="light" icon={<IconCheck size={16} />} mt="xs">{brandImportResult.imported} brand(s) imported.</Alert>
              )}
            </Box>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { closeBrandImport(); setBrandImportFile(null); setBrandImportResult(null) }}>{t("config.close","Close")}</Button>
            <Button leftSection={<IconUpload size={16} />} disabled={!brandImportFile || !!brandImportResult}
              loading={brandImporting} onClick={handleBrandImport}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t("config.import","Import")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Vendor Import Modal (ADMIN only) ── */}
      <Modal opened={vendorImportOpen} onClose={() => { closeVendorImport(); setVendorImportFile(null); setVendorImportResult(null) }}
        title={<Group gap="xs"><IconFileSpreadsheet size={20} /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{t('config.importVendors','Import Vendors')}</Text></Group>}
        size="md" radius="lg">
        <Stack gap="md">
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={600} mb={4}>Required: name</Text>
            <Text size="xs" c="dimmed">Optional: contactName, phone, email, address</Text>
          </Alert>
          <Button variant="subtle" size="xs" leftSection={<IconUpload size={14} />} onClick={downloadVendorTemplate}>
            {t("config.downloadCsvTemplate","Download CSV Template")}
          </Button>
          <Divider />
          <FileButton onChange={(f) => { setVendorImportFile(f); setVendorImportResult(null) }} accept=".csv,.xlsx,.xls">
            {(props) => (
              <Button {...props} variant="outline" leftSection={<IconFileSpreadsheet size={16} />} color="gray">
                {vendorImportFile ? vendorImportFile.name : t("config.chooseFileCsvXlsx","Choose File (.csv / .xlsx)")}
              </Button>
            )}
          </FileButton>
          {vendorImportResult && (
            <Box>
              <Group gap="lg" mb="xs">
                <Box ta="center"><Text size="xl" fw={600} c="lime">{vendorImportResult.imported}</Text><Text size="xs" c="dimmed">{t("config.imported","Imported")}</Text></Box>
                <Box ta="center"><Text size="xl" fw={600} c="orange">{vendorImportResult.skipped}</Text><Text size="xs" c="dimmed">{t("config.skipped","Skipped")}</Text></Box>
              </Group>
              {vendorImportResult.errors.length > 0 && (
                <ScrollArea h={120}><List size="xs" spacing={2}>
                  {vendorImportResult.errors.map((e, i) => <List.Item key={i} icon={<IconAlertCircle size={12} color="orange" />}>{e}</List.Item>)}
                </List></ScrollArea>
              )}
              {vendorImportResult.imported > 0 && (
                <Alert color="teal" variant="light" icon={<IconCheck size={16} />} mt="xs">{vendorImportResult.imported} vendor(s) imported.</Alert>
              )}
            </Box>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { closeVendorImport(); setVendorImportFile(null); setVendorImportResult(null) }}>{t("config.close","Close")}</Button>
            <Button leftSection={<IconUpload size={16} />} disabled={!vendorImportFile || !!vendorImportResult}
              loading={vendorImporting} onClick={handleVendorImport}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t("config.import","Import")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
